import { useState, useCallback, useRef, useEffect, useMemo, useContext } from 'react'
import {
  View,
  Text,
  ActivityIndicator,
  TextInput as RNTextInput,
  Linking,
  Platform,
} from 'react-native'
import { useRouter, useLocalSearchParams, usePathname } from 'expo-router'
import { NavigationContext } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { notification, selection, NotificationType } from '@/lib/utils/haptics'
import {
  Clock,
  AlertCircle,
  CheckCircle,
  Tag,
  X,
  MapPin,
  Pencil,
  CreditCard,
  Wallet,
} from '@/components/ui/icons'
import { FONTS, COLORS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors, Z_INDEX, INTERACTION, withAlpha } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { env } from '@/lib/env'
import { isPaypalOffered } from '@/lib/payments/paypal-offered'
import { guestChargeCents } from '@/lib/payments/processor-fee'
import { CheckoutPayFooter } from '@/components/payments/CheckoutPayFooter'
import { CheckoutMethodPicker } from '@/components/payments/CheckoutMethodPicker'
import { CheckoutSecureHeader } from '@/components/payments/CheckoutSecureHeader'
import { useBookingStore } from '@/lib/stores/booking.store'
import { useValidatePromo } from '@/lib/hooks/use-promo'
import { useVehicleDetail } from '@/lib/hooks/use-vehicle-detail'
import { useAuth } from '@/lib/hooks/use-auth'
import { fullProfileName } from '@/lib/utils/profile-display'
import { useAmlGate } from '@/lib/hooks/use-aml-gate'
import { GateFallback } from '@/components/ui/GateFallback'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { useBooking, useCreateBooking, useReleaseUnpaidHold } from '@/lib/hooks/use-booking'
import {
  usePaymentMethods,
  useProcessCardPayment,
  useProcessPayPalPayment,
} from '@/lib/hooks/use-payment'
import { supabase } from '@/lib/supabase'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { checkoutPayerFromProfile } from '@/lib/payments/charge-result'
import { isPayControlLocked, isPaymentRequestInFlight } from '@/lib/payments/flight'
import {
  classifyUnknownPaymentFailure,
  paymentOutcomePresentation,
} from '@/lib/payments/outcome-presentation'
import type { PaymentFailureCode } from '@/lib/payments/codes'
import { calculateProfileCompletion } from '@/lib/utils/profile-completion'
import { isInspectionPhotoRequirement } from '@/lib/types/listing-photo.types'
import { tripPriceForVehicle } from '@/lib/utils/pricing'
import { usePairFee } from '@/lib/hooks/use-pair-fee'
import { RepeatPairFeeCard } from '@/components/booking/RepeatPairFeeCard'
import { CoverageStayReasonCard } from '@/components/booking/CoverageStayReasonCard'
import {
  bookingDateKey,
  bookingTimeHHmm,
  formatSvDateDisplay,
  toBookingDateTimeISO,
  rentalDaysBetween,
  isBookingDateTimeInThePast,
} from '@/lib/utils/dates'
import { formatTime12h } from '@/lib/utils/time-slots'
import { formatCurrency } from '@/lib/utils/format'
import { normalizeVehicleMinRentalHours } from '@/lib/utils/min-rental-duration'
import { startsTooSoonForHostApproval } from '@/lib/utils/host-approval-policy'
import {
  INDEPENDENCE_CAMPAIGN,
  calculateIndependenceCampaignDiscountCents,
  isIndependenceCampaignCode,
} from '@/lib/utils/pricing-core'
import { Image } from '@/components/ui/Image'
import { PhotoLightbox, usePhotoLightbox } from '@/components/ui/PhotoLightbox'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Pressable } from '@/components/ui/Pressable'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { InsurerBadge } from '@/components/insurance/InsurerBadge'
import { CoverageAssistanceCard } from '@/components/insurance/CoverageAssistanceCard'
import { AnimatedSpinner } from '@/components/ui/AnimatedSpinner'
import { CardForm } from '@/components/booking/CardForm'
import {
  CheckoutEditorialRow,
  CheckoutPaymentRail,
  CheckoutSummaryColumn,
} from '@/components/booking/CheckoutEditorialLayout'
import { CheckoutVehicleHero } from '@/components/booking/CheckoutVehicleHero'
import {
  BookingLocationPicker,
  type BookingLocationPickerMode,
} from '@/components/booking/BookingLocationPicker'
import {
  areBookingLocationsWithinRadius,
  resolveBookingLocations,
  type BookingLocationValue,
} from '@/lib/utils/booking-location'
import { ThreeDsModal } from '@/components/booking/ThreeDsModal'
import { useProductTelemetryScroll } from '@/lib/hooks/use-product-telemetry-scroll'
import { bookingService } from '@/lib/services/booking.service'
import { legalService } from '@/lib/services/legal.service'
import { useCurrentRentalTerms } from '@/lib/hooks/use-rental-terms'
import { RentalTermsDocuments } from '@/components/legal/RentalTermsDocuments'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import type { CardPaymentData, PaymentMethod } from '@/lib/types/payment.types'
import { useBookingEligibility } from '@/lib/hooks/use-booking-eligibility'
import { vehicleHasPlatformCoverage } from '@/lib/utils/qualitas-asr'
import { verificationStepFor } from '@/lib/verification/steps'
import { markBookingComplianceDetour } from '@/lib/navigation/booking-detour'

type CheckoutLocation = {
  address: string
  lat: number
  lng: number
}

function formatPaymentMethodExpiry(method: PaymentMethod): string {
  if (!method.cardExpMonth || !method.cardExpYear) return '--/--'
  return `${String(method.cardExpMonth).padStart(2, '0')}/${String(method.cardExpYear).slice(-2)}`
}

function paymentBrandLabel(method: PaymentMethod, fallback: string): string {
  if (!method.cardBrand || method.cardBrand === 'unknown') return fallback
  return method.cardBrand === 'mastercard' ? 'Mastercard' : method.cardBrand.toUpperCase()
}

function expectedCvvLength(method: PaymentMethod | null): number {
  return method?.cardBrand === 'amex' ? 4 : 3
}

function formatTripRangeLabel(params: {
  startDate: string
  endDate: string
  startTime: string | null
  endTime: string | null
}): string {
  const start = `${formatSvDateDisplay(params.startDate)}${
    params.startTime ? ` ${formatTime12h(params.startTime)}` : ''
  }`
  const end = `${formatSvDateDisplay(params.endDate)}${
    params.endTime ? ` ${formatTime12h(params.endTime)}` : ''
  }`
  return `${start} - ${end}`
}

function amountBucket(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return 'unknown'
  if (cents < 2500) return 'lt_25'
  if (cents < 5000) return '25_49'
  if (cents < 10000) return '50_99'
  if (cents < 20000) return '100_199'
  return 'gte_200'
}

/**
 * 'refunded'        the charge went through against a rental that had stopped
 *                   waiting for it, and the money was voided at the processor.
 * 'refund_pending'  same, but the processor refused the void — a human owes the guest
 *                   a refund, and saying "done" would be a lie.
 */
type ConfirmOutcome = 'captured' | 'refunded' | 'refund_pending' | 'failed' | null
type ConfirmResult = { status: ConfirmOutcome; reason?: string | null }

async function confirmProviderPayment(params: {
  rentalId: string
  transactionId: string
}): Promise<ConfirmResult> {
  for (let attempt = 0; attempt < 8; attempt++) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 2000))

    const { data } = await supabase.functions.invoke('confirm-payment', {
      body: {
        rentalId: params.rentalId,
        transactionId: params.transactionId,
      },
    })
    const reason =
      typeof data?.statusReason === 'string' && data.statusReason.trim()
        ? data.statusReason.trim()
        : null
    if (data?.status === 'captured') return { status: 'captured', reason }
    if (data?.status === 'failed') return { status: 'failed', reason }
    // The server charged, found the rental gone, and reversed it. This MUST be
    // read before the fallback below: the payment row is deliberately left
    // 'captured' so refund-payment can still see it, so a fallback that only
    // looks at the payment would call this a successful booking.
    if (data?.status === 'refund_required') {
      return {
        status: data.refunded === true ? 'refunded' : 'refund_pending',
        reason,
      }
    }

    // Fallback for a poll that got no answer — the webhook may have resolved
    // the payment out from under us. A captured payment is only a success if
    // the RENTAL entered a valid paid state; captured-but-dead is the
    // dead-rental case, which is a refund, not a booking. Checking the payment
    // alone is what turned a reversed charge into "¡Reserva confirmada!".
    const { data: payment } = await supabase
      .from('payments')
      .select('status')
      .eq('rental_id', params.rentalId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (payment?.status === 'failed') return { status: 'failed', reason }
    if (payment?.status === 'captured') {
      const { data: rental } = await supabase
        .from('rentals')
        .select('status')
        .eq('id', params.rentalId)
        .maybeSingle()
      if (rental?.status === 'confirmed' || rental?.status === 'awaiting_host') {
        return { status: 'captured', reason }
      }
      if (rental?.status && rental.status !== 'pending_payment') {
        return { status: 'refund_pending', reason }
      }
    }
  }

  return { status: null, reason: null }
}

export default function CheckoutScreen() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, isTablet } = useBreakpoint()
  // isDesktop is large WEB only. iPad / Android tablets are isTablet even
  // past 1024. The two-column checkout is both — never gate it on isDesktop
  // alone or native tablets stay a stretched phone.
  const isEditorial = isDesktop || isTablet
  const insets = useSafeAreaInsets()
  const lightbox = usePhotoLightbox()
  const router = useRouter()
  const pathname = usePathname()
  const params = useLocalSearchParams<{ rentalId?: string }>()
  const recoveryRentalId =
    typeof params.rentalId === 'string' && params.rentalId.trim() ? params.rentalId.trim() : null

  const {
    vehicleId,
    startDate,
    endDate,
    startTime,
    endTime,
    insuranceTier,
    promoCode: draftPromoCode,
    pricing: draftPricing,
    setPricing,
    pickupAddress,
    pickupLat,
    pickupLng,
    dropoffAddress,
    dropoffLat,
    dropoffLng,
    isSameLocation,
    setPickup,
    setDropoff,
    setSameLocation,
  } = useBookingStore()

  const {
    data: recoveredBooking,
    isLoading: isRecoveringBooking,
    isError: isRecoveryError,
  } = useBooking(recoveryRentalId ?? undefined)

  // Merge date + time into full ISO datetime strings. If checkout is opened
  // from a payment retry/deep link, recover the canonical dates from the
  // pending rental instead of trusting a local draft that may have expired.
  // UNA FECHA SIN HORA NO ES UN INSTANTE, y la rama de fallo devolvia la fecha
  // pelada. Con `'2026-09-10'` en vez de `null`, todas las compuertas de este
  // archivo que preguntan `!!startDateTime` --hasCheckoutData, el throw de
  // handleConfirm, selectedDurationHours-- daban por bueno un valor que no tiene
  // hora: la duracion se media desde medianoche UTC y `createBooking` recibia
  // `start_at = '2026-09-10'`, que Postgres fija a medianoche, no a la hora que
  // el huesped nunca eligio.
  //
  // Antes quedaba tapado por casualidad: el vacio era `''`, que es falsy, asi que
  // la expresion entera se apagaba sola. Al normalizar las fechas a `null` la
  // casualidad desaparece y el defecto queda expuesto -- por eso se arregla acá y
  // no se deja "porque nunca pasa". La pantalla de reservar exige las dos horas,
  // pero el checkout se monta por su cuenta desde payment-complete.tsx y desde
  // insurance.tsx, y en web la ruta es navegable con el borrador persistido.
  const draftStartDateTime =
    startDate && startTime ? toBookingDateTimeISO(startDate, startTime) : null
  const draftEndDateTime = endDate && endTime ? toBookingDateTimeISO(endDate, endTime) : null
  const startDateTime = recoveryRentalId ? (recoveredBooking?.startAt ?? null) : draftStartDateTime
  const endDateTime = recoveryRentalId ? (recoveredBooking?.endAt ?? null) : draftEndDateTime
  const displayStartDate = recoveryRentalId ? bookingDateKey(recoveredBooking?.startAt) : startDate
  const displayEndDate = recoveryRentalId ? bookingDateKey(recoveredBooking?.endAt) : endDate
  const displayStartTime = recoveryRentalId ? bookingTimeHHmm(recoveredBooking?.startAt) : startTime
  const displayEndTime = recoveryRentalId ? bookingTimeHHmm(recoveredBooking?.endAt) : endTime
  const isRecoveredCheckout = Boolean(recoveredBooking)
  const activeVehicleId = recoveryRentalId ? (recoveredBooking?.vehicleId ?? null) : vehicleId
  const { vehicle } = useVehicleDetail(activeVehicleId ?? undefined)
  const hostId = recoveredBooking?.hostId ?? vehicle?.host.id
  const { guestServiceFeePct, completedPairTrips, isReady: pairFeeReady } = usePairFee(hostId)
  const pricing = recoveryRentalId ? (recoveredBooking?.pricing ?? null) : draftPricing
  const paypalOffered = isPaypalOffered()
  // End users must complete Qualitas AML before paying for a trip that CARRIES
  // platform coverage (P3). The declaration keys off the vehicle's coverage —
  // own-policy/exempt bookings pay no premium and owe no declaration.
  const bookingCarriesPlatformCoverage = vehicle ? vehicleHasPlatformCoverage(vehicle) : null
  const { gated: amlGated } = useAmlGate('guest', {
    requiresPlatformCoverage: bookingCarriesPlatformCoverage,
  })
  const minRentalHours = useMemo(
    () => normalizeVehicleMinRentalHours(vehicle?.minRentalHours),
    [vehicle?.minRentalHours],
  )
  const { profile } = useAuth()
  const { createBooking, isPending: isCreating } = useCreateBooking()
  const { releaseUnpaidHold } = useReleaseUnpaidHold()
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)
  const { mutateAsync: processCardPayment, isPending: isProcessingCardPayment } =
    useProcessCardPayment()
  const { mutateAsync: processPayPalPayment, isPending: isProcessingPayPalPayment } =
    useProcessPayPalPayment()
  const { data: savedPaymentMethods = [], isLoading: isLoadingPaymentMethods } = usePaymentMethods()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsError, setTermsError] = useState<string | undefined>()
  const [staleHoldId, setStaleHoldId] = useState<string | null>(null)
  const [isRestartingStaleHold, setIsRestartingStaleHold] = useState(false)
  const {
    data: currentTerms,
    isLoading: termsLoading,
    refetch: refetchTerms,
  } = useCurrentRentalTerms()
  useEffect(() => {
    // A checkbox never carries consent across a new contractual version.
    setTermsAccepted(false)
  }, [currentTerms?.version])
  const [paymentRail, setPaymentRail] = useState<'paypal' | 'card'>('card')
  const guestQuote = useMemo(
    () =>
      guestChargeCents({
        marketplaceCents: pricing?.totalCents ?? 0,
        method: paymentRail,
        includedProcessorCents: pricing?.platformFeeProcessingCents ?? 0,
      }),
    [pricing?.totalCents, pricing?.platformFeeProcessingCents, paymentRail],
  )
  const chargedCents = guestQuote.chargeCents
  const [hostedCheckoutUrl, setHostedCheckoutUrl] = useState<string | null>(null)
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null)
  const [savedCardCvv, setSavedCardCvv] = useState('')
  const [threeDsChallenge, setThreeDsChallenge] = useState<{
    url: string
    rentalId: string
    transactionId: string
  } | null>(null)
  // `confirmProviderPayment` returning null is NOT a decline — it means we
  // polled the provider out and still do not know. That outcome gets its own
  // wording and its own next step (check Mis viajes), never "Pago rechazado",
  // because a guest told their card was declined pays a second time.
  const [unconfirmedRentalId, setUnconfirmedRentalId] = useState<string | null>(null)
  // A charge that succeeded against a rental that was already gone. Terminal
  // like `unconfirmedRentalId`, and deliberately NOT the confirmation screen:
  // the guest paid, there is no booking, and the money is on its way back.
  const [refundedPayment, setRefundedPayment] = useState<{ settled: boolean } | null>(null)
  /*
   * EL AVISO TIENE QUE APARECER DONDE ESTA LA PERSONA.
   *
   * Cuando el cobro se aprueba contra una reserva que ya murio, se anula y se
   * pinta un `CheckoutNotice` -- arriba del scroll. Pero el toque de "Pagar"
   * ocurre al FONDO del formulario de tarjeta, a mas de mil pixeles de ahi: en
   * su pantalla el boton se pone gris y no pasa nada mas. Le movimos el dinero
   * y no se lo dijimos donde podia verlo.
   *
   * El caso hermano --"no sabemos si te cobramos"-- ya abre un dialogo, que
   * aparece donde sea que estes. Este quedo sin el. Ahora usa el mismo mecanismo
   * con su propio texto: no es lo mismo "no sabemos" que "te lo devolvimos".
   */
  const [isRefundDialogVisible, setIsRefundDialogVisible] = useState(false)
  const [isUnconfirmedDialogVisible, setIsUnconfirmedDialogVisible] = useState(false)
  const [paymentOutcomeCode, setPaymentOutcomeCode] = useState<PaymentFailureCode | null>(null)
  const holdToReleaseRef = useRef<string | null>(null)
  const [locationPickerMode, setLocationPickerMode] = useState<BookingLocationPickerMode | null>(
    null,
  )

  // Promo code
  // A promo the person already applied survives leaving this screen: the store
  // persists it (booking.store.ts partialize) and the checkout writes it there
  // on success. Only the two local pieces of state were starting from scratch,
  // so coming back from the vehicle page or a card sheet silently dropped a
  // discount somebody had earned — and the total went up with no explanation.
  const [promoInput, setPromoInput] = useState(draftPromoCode ?? '')
  // The CODE comes back; the "applied" badge does not.
  //
  // Restoring both looked right and was worse than doing nothing: the badge is
  // a claim about the price, and the discount itself is only produced by the
  // validation call this screen makes when Aplicar is pressed. Seeding
  // `promoApplied` from the draft therefore showed "Promo aplicado" over a
  // total that did not include it — telling somebody they got a discount while
  // charging them full price.
  //
  // So the typing is what is saved, which was the actual complaint, and the
  // claim is left to the code path that can make it true.
  const [promoApplied, setPromoApplied] = useState(false)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [promoDiscountCents, setPromoDiscountCents] = useState(0)
  const validatePromo = useValidatePromo()
  const [pendingRentalId, setPendingRentalId] = useState<string | null>(null)
  const freeUnpaidHold = useCallback(
    async (rentalId: string | null | undefined) => {
      if (!rentalId) return
      try {
        const released = await releaseUnpaidHold(rentalId)
        if (released) {
          setPendingRentalId((current) => (current === rentalId ? null : current))
        }
      } catch (err) {
        captureError(err, { screen: 'booking/checkout', action: 'release-unpaid-hold' })
      }
    },
    [releaseUnpaidHold],
  )

  const presentPaymentFailure = useCallback(
    (err: unknown, fallbackCode: PaymentFailureCode = 'UNKNOWN') => {
      const classified = classifyUnknownPaymentFailure(err)
      setPaymentOutcomeCode(
        classified.code === 'UNKNOWN' && fallbackCode !== 'UNKNOWN'
          ? fallbackCode
          : classified.code,
      )
    },
    [],
  )

  // P0-1: Synchronous double-submit guard — prevents race between tap and async isPending
  const isSubmittingRef = useRef(false)
  const allowPaymentNavigationRef = useRef(false)
  const allowLeaveWithoutRelease = useCallback(() => {
    allowPaymentNavigationRef.current = true
    holdToReleaseRef.current = null
  }, [])
  const savedCardsAvailableForCheckout = useMemo(
    () => (env.isSavedCardChargesEnabled ? savedPaymentMethods : []),
    [savedPaymentMethods],
  )
  const selectedPaymentMethod = useMemo(
    () =>
      savedCardsAvailableForCheckout.find((method) => method.id === selectedPaymentMethodId) ??
      null,
    [savedCardsAvailableForCheckout, selectedPaymentMethodId],
  )
  const usingSavedPaymentMethod = Boolean(selectedPaymentMethod)

  useEffect(() => {
    if (savedCardsAvailableForCheckout.length === 0) {
      if (selectedPaymentMethodId) setSelectedPaymentMethodId(null)
      return
    }
    const stillExists = savedCardsAvailableForCheckout.some(
      (method) => method.id === selectedPaymentMethodId,
    )
    if (!stillExists) {
      setSelectedPaymentMethodId(
        savedCardsAvailableForCheckout.find((method) => method.isDefault)?.id ??
          savedCardsAvailableForCheckout[0]?.id ??
          null,
      )
      setSavedCardCvv('')
    }
  }, [savedCardsAvailableForCheckout, selectedPaymentMethodId])

  // Days between start/end for pricing math. Pure, only depends on ISO dates.
  //
  // Was a raw millisecond division rounded to the nearest day — a fourth
  // implementation of "how long is this rental", outside the parity check that
  // covers the other three. It agrees with the charge today only because these
  // two values are always date-only SV keys, where a UTC-midnight delta happens
  // to equal the calendar-day delta. Hand either one a timestamp and the number
  // the guest is quoted stops matching the number they are charged.
  const rentalDays = useMemo(() => {
    if (!displayStartDate || !displayEndDate) return 0
    return rentalDaysBetween(new Date(displayStartDate), new Date(displayEndDate))
  }, [displayStartDate, displayEndDate])

  const preDiscountPricing = useMemo(() => {
    if (!vehicle || rentalDays < 1) return null
    return tripPriceForVehicle(
      {
        ...vehicle,
        // DESACOPLE DE LA PRIMA (P3): la línea de cobertura se decide por la
        // cobertura del vehículo, igual que en booking.service y en el cargo
        // autoritativo (insuranceCents = 0 para own_policy/exempt).
        hasPlatformCoverage: bookingCarriesPlatformCoverage,
      },
      {
        days: rentalDays,
        insuranceTier,
        promoDiscountCents: 0,
        guestServiceFeePct,
      },
    )
  }, [vehicle, rentalDays, insuranceTier, bookingCarriesPlatformCoverage, guestServiceFeePct])

  const independenceCampaignDiscountCents = useMemo(() => {
    if (
      isRecoveredCheckout ||
      promoApplied ||
      !startDateTime ||
      !endDateTime ||
      !preDiscountPricing
    )
      return 0
    return calculateIndependenceCampaignDiscountCents({
      startAt: startDateTime,
      endAt: endDateTime,
      serviceFeeCents: preDiscountPricing.platformFeeCents,
    })
  }, [endDateTime, isRecoveredCheckout, preDiscountPricing, promoApplied, startDateTime])

  const isRecoveredIndependenceCampaign =
    isRecoveredCheckout &&
    isIndependenceCampaignCode(recoveredBooking?.discountCode) &&
    (pricing?.promoDiscountCents ?? 0) > 0
  const isIndependenceCampaignApplied =
    independenceCampaignDiscountCents > 0 || isRecoveredIndependenceCampaign
  const effectivePromoDiscountCents = promoApplied
    ? promoDiscountCents
    : independenceCampaignDiscountCents
  const appliedPromoCode = promoApplied
    ? promoInput.trim().toUpperCase()
    : isIndependenceCampaignApplied
      ? INDEPENDENCE_CAMPAIGN.code
      : null

  // Sync pricing into the booking store whenever an input that affects the
  // total changes.
  useEffect(() => {
    if (isRecoveredCheckout) return
    if (!vehicle || rentalDays < 1) return
    const breakdown = tripPriceForVehicle(
      {
        ...vehicle,
        hasPlatformCoverage: bookingCarriesPlatformCoverage,
      },
      {
        days: rentalDays,
        insuranceTier,
        promoDiscountCents: effectivePromoDiscountCents,
        guestServiceFeePct,
      },
    )
    setPricing(breakdown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vehicle,
    rentalDays,
    insuranceTier,
    effectivePromoDiscountCents,
    isRecoveredCheckout,
    guestServiceFeePct,
  ])

  // `confirmProviderPayment` polls the provider up to 8 times with 2s between
  // attempts — as much as 16 seconds AFTER the charge call resolves. Without
  // this flag the overlay vanished and the Pay button re-enabled for that whole
  // window, while the charge was still being confirmed. A user who tapped again
  // was aiming at a second payment.
  //
  // A request is genuinely running right now. This is the ONLY state that may
  // paint the blocking scrim or veto navigation, because it is the only state
  // where leaving would strand work in flight.
  const isPaymentInFlight = isPaymentRequestInFlight({
    creatingHold: isCreating,
    charging: isProcessingCardPayment || isProcessingPayPalPayment,
    confirming: isConfirmingPayment,
  })

  // `unconfirmedRentalId` is a TERMINAL state, not an in-flight one. When the
  // provider poll runs out without an answer the screen tells the guest "no lo
  // intentés de nuevo" — and it used to leave the Pay button live underneath
  // that very sentence, because isConfirmingPayment had already flipped back in
  // its finally block. Paying twice was one tap away from a dialog asking them
  // not to. So it still disables the pay controls.
  //
  // What it must NOT do is what it used to: `setUnconfirmedRentalId` is never
  // reset, so folding it into the in-flight flag made the full-screen scrim and
  // the `beforeRemove` veto permanent. Tapping "Cerrar" on the dialog left the
  // guest sealed on the money screen — header back, Android back, iOS swipe and
  // browser back all vetoed, spinner on top of the back button — with only a
  // force-quit to escape. Nothing is in flight at that point; there is no
  // in-flight work for a navigation veto to protect. Disable paying, not leaving.
  const isPaymentUnresolved = unconfirmedRentalId !== null || refundedPayment !== null

  /** Pay controls stay locked in both states — one charge is the invariant. */
  const isPending = isPayControlLocked({
    creatingHold: isCreating,
    charging: isProcessingCardPayment || isProcessingPayPalPayment,
    confirming: isConfirmingPayment,
    unresolved: isPaymentUnresolved,
  })
  const profileCompletion = useMemo(() => calculateProfileCompletion(profile), [profile])

  // A pending rental is reusable only for the exact checkout inputs that
  // produced it. If the guest changes a monetized option after a failed payment
  // attempt, force createBooking() to build a fresh rental so DB totals,
  // discount_code, and line items stay in lockstep with the UI.
  useEffect(() => {
    setPendingRentalId(null)
  }, [
    vehicleId,
    startDateTime,
    endDateTime,
    pickupAddress,
    pickupLat,
    pickupLng,
    dropoffAddress,
    dropoffLat,
    dropoffLng,
    isSameLocation,
    insuranceTier,
    appliedPromoCode,
  ])

  // P1-11: Prevent back navigation while payment is in flight.
  //
  // The listener is installed once and decides at EVENT time, from refs. The
  // previous shape guarded installation on `isPending || isSubmittingRef.current`
  // — a ref, which cannot be an effect dependency. `isSubmittingRef.current`
  // going back to false in a finally block triggers no render, so it could not
  // re-run the effect to tear the listener down: any render that installed the
  // listener on the strength of the ref left a veto behind that nothing removed.
  // A listener that is always mounted and always re-reads the current state has
  // no stale-decision window at all.
  // TAGO-MOBILE-3M / TAGO-WEB-1W: `useNavigation()` throws if this screen
  // mounts without a NavigationContainer (web explore deep links, React 19).
  // NavigationContext is null in that case; skip the veto instead of crashing.
  const navigation = useContext(NavigationContext)
  const isPaymentInFlightRef = useRef(false)
  useEffect(() => {
    isPaymentInFlightRef.current = isPaymentInFlight
  }, [isPaymentInFlight])
  useEffect(() => {
    if (!navigation) return
    const unsubscribe = navigation.addListener(
      'beforeRemove',
      (e: { preventDefault: () => void }) => {
        if (allowPaymentNavigationRef.current) return
        // Only in-flight work justifies trapping the guest. A terminal
        // unconfirmed payment must always be walkable-away-from.
        if (isPaymentInFlightRef.current || isSubmittingRef.current) {
          e.preventDefault()
          return
        }
        const rentalId = holdToReleaseRef.current
        if (!rentalId) return
        holdToReleaseRef.current = null
        void releaseUnpaidHold(rentalId).catch((err) => {
          captureError(err, { screen: 'booking/checkout', action: 'release-hold-on-leave' })
        })
      },
    )
    return unsubscribe
  }, [navigation, releaseUnpaidHold])

  useEffect(() => {
    if (allowPaymentNavigationRef.current || unconfirmedRentalId || refundedPayment) {
      holdToReleaseRef.current = null
      return
    }
    holdToReleaseRef.current = threeDsChallenge?.rentalId ?? pendingRentalId
  }, [pendingRentalId, refundedPayment, threeDsChallenge, unconfirmedRentalId])

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return
    const onPageHide = () => {
      if (allowPaymentNavigationRef.current) return
      const rentalId = holdToReleaseRef.current
      if (!rentalId) return
      holdToReleaseRef.current = null
      void releaseUnpaidHold(rentalId).catch((err) => {
        captureError(err, { screen: 'booking/checkout', action: 'release-hold-on-pagehide' })
      })
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [releaseUnpaidHold])

  const draftResolvedLocations = useMemo(
    () =>
      resolveBookingLocations({
        vehicleHomeAddress: vehicle?.homeAddress,
        vehicleHomeLocation: vehicle?.homeLocation,
        pickupAddress,
        pickupLat,
        pickupLng,
        dropoffAddress,
        dropoffLat,
        dropoffLng,
      }),
    [
      dropoffAddress,
      dropoffLat,
      dropoffLng,
      pickupAddress,
      pickupLat,
      pickupLng,
      vehicle?.homeAddress,
      vehicle?.homeLocation,
    ],
  )

  const recoveredPickupLocation = useMemo<CheckoutLocation | null>(() => {
    if (
      !recoveredBooking?.pickupAddress ||
      recoveredBooking.pickupLat == null ||
      recoveredBooking.pickupLng == null
    ) {
      return null
    }
    return {
      address: recoveredBooking.pickupAddress,
      lat: recoveredBooking.pickupLat,
      lng: recoveredBooking.pickupLng,
    }
  }, [recoveredBooking])

  const recoveredDropoffLocation = useMemo<CheckoutLocation | null>(() => {
    if (
      !recoveredBooking?.dropoffAddress ||
      recoveredBooking.dropoffLat == null ||
      recoveredBooking.dropoffLng == null
    ) {
      return null
    }
    return {
      address: recoveredBooking.dropoffAddress,
      lat: recoveredBooking.dropoffLat,
      lng: recoveredBooking.dropoffLng,
    }
  }, [recoveredBooking])

  const {
    hasVehicleHomeLocation: draftHasVehicleHomeLocation,
    pickupLocation: draftPickupLocation,
    dropoffLocation: draftDropoffLocation,
  } = draftResolvedLocations
  const hasVehicleHomeLocation = isRecoveredCheckout || draftHasVehicleHomeLocation
  const effectivePickupLocation = recoveredPickupLocation ?? draftPickupLocation
  const effectiveDropoffLocation = recoveredDropoffLocation ?? draftDropoffLocation

  const deliveryRadiusKm = vehicle?.deliveryRadiusKm ?? 10
  const locationsWithinRadius = useMemo(() => {
    if (isRecoveredCheckout) return Boolean(effectivePickupLocation && effectiveDropoffLocation)
    return areBookingLocationsWithinRadius({
      hostLocation: vehicle?.homeLocation,
      pickupLocation: effectivePickupLocation,
      dropoffLocation: effectiveDropoffLocation,
      deliveryRadiusKm,
    })
  }, [
    deliveryRadiusKm,
    effectiveDropoffLocation,
    effectivePickupLocation,
    isRecoveredCheckout,
    vehicle?.homeLocation,
  ])

  const selectedDurationHours = useMemo(() => {
    if (!startDateTime || !endDateTime) return 0
    const start = new Date(startDateTime).getTime()
    const end = new Date(endDateTime).getTime()
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0
    return Math.round((end - start) / 3_600_000)
  }, [endDateTime, startDateTime])

  const meetsVehicleMinimumDuration =
    isRecoveredCheckout || !minRentalHours || selectedDurationHours >= minRentalHours

  /**
   * SAY IT BEFORE THE CARD FORM, NOT AFTER THE CHARGE IS REFUSED.
   *
   * `create-payment` has always enforced verification, and it always will — a control that runs
   * in the app is a suggestion. But it refused with an uncoded 422, so the client had no way to
   * attach copy and this screen fell back to "Pago rechazado. Intentá de nuevo o usá otro método
   * de pago." A guest who had merely never verified was told their CARD was declined: they
   * blamed their bank, tried a second card, and left. Twenty-one of nine hundred sixty-three
   * people in this app can currently pay, and every one of the rest met that message.
   *
   * Same server function, asked early. FAILS OPEN: while it is loading or if it errors,
   * `resolved` is false and this screen behaves exactly as it did — the server still has the
   * final word, and blocking a verified guest on a dropped connection would be the worse bug.
   */
  const eligibility = useBookingEligibility(bookingCarriesPlatformCoverage, {
    vehicleId: activeVehicleId,
    startAt: startDateTime,
    endAt: endDateTime,
  })
  const complianceBlocked = eligibility.resolved && !eligibility.allowed

  const recoveredRentalPayable = !recoveredBooking || recoveredBooking.status === 'pending_payment'
  const startIsPast = Boolean(
    displayStartDate &&
    displayStartTime &&
    isBookingDateTimeInThePast(displayStartDate, displayStartTime),
  )
  const startsTooSoon = Boolean(startDateTime && startsTooSoonForHostApproval(startDateTime))
  const hasCheckoutData =
    !!activeVehicleId &&
    !!startDateTime &&
    !!endDateTime &&
    !!pricing &&
    !!vehicle &&
    !!effectivePickupLocation &&
    !!effectiveDropoffLocation &&
    locationsWithinRadius &&
    meetsVehicleMinimumDuration &&
    recoveredRentalPayable &&
    !startIsPast &&
    !startsTooSoon &&
    !isRecoveringBooking &&
    !isRecoveryError &&
    (isRecoveredCheckout || pairFeeReady) &&
    // Folded in here so `ensureBooking()` cannot run either: today an ineligible guest mints a
    // `pending_payment` rental — RLS checks only that they are the caller and the vehicle is
    // active — and then the charge is refused, leaving a row behind for a trip that never was.
    !complianceBlocked

  const complianceBlockedMessage = useMemo(() => {
    if (!complianceBlocked) return null
    if (eligibility.missing.length === 0 || eligibility.hasUnknownBlocker) {
      // A newer server naming a condition this build has no copy for. Admitting that beats
      // rendering an empty list next to a disabled button.
      return t('payment.booking_blocked_unknown')
    }
    /*
     * Del REGISTRO, no de un mapa propio de esta pantalla.
     *
     * Aca vivia `payment.blocker_*`, un tercer juego de etiquetas para los
     * mismos seis tramites: el hub decia "Documento de identidad", esta
     * pantalla "Verifica tu DUI o pasaporte" y la ficha otra cosa. Tres nombres
     * para el mismo tramite son tres tramites, para quien los lee.
     *
     * Los estados que no son pasos --revision pendiente, cuenta en borrado--
     * conservan su texto propio: no se pueden "hacer" y merecen otra frase.
     */
    return eligibility.missing
      .map((reason) => {
        const step = verificationStepFor(reason)
        return step ? t(step.labelKey) : t(`payment.blocker_${reason}`)
      })
      .join('\n')
  }, [complianceBlocked, eligibility.hasUnknownBlocker, eligibility.missing, t])

  const checkoutBlockedMessage = useMemo(() => {
    // First, because it is the most specific thing we know: "te falta la licencia" is worth
    // more than "faltan datos de la reserva", and it is the reason the tap will not work.
    if (complianceBlockedMessage) return complianceBlockedMessage
    if (isRecoveringBooking) return t('checkout.resume_loading')
    if (isRecoveryError) return t('checkout.resume_error')
    if (recoveredBooking && !recoveredRentalPayable) return t('checkout.resume_already_handled')
    if (!hasVehicleHomeLocation) return t('checkout.vehicle_location_missing')
    if (!locationsWithinRadius) return t('checkout.location_out_of_radius')
    if (!meetsVehicleMinimumDuration) return t('checkout.min_rental_duration')
    if (startIsPast) return t('checkout.start_in_the_past')
    if (startsTooSoon) return t('rental_terms_flow.minimum_lead')
    if (!isRecoveredCheckout && !pairFeeReady) return t('checkout.pair_fee_loading')
    return t('checkout.missing_booking_data')
  }, [
    complianceBlockedMessage,
    hasVehicleHomeLocation,
    isRecoveredCheckout,
    isRecoveringBooking,
    isRecoveryError,
    locationsWithinRadius,
    meetsVehicleMinimumDuration,
    pairFeeReady,
    recoveredBooking,
    recoveredRentalPayable,
    startIsPast,
    startsTooSoon,
    t,
  ])

  const showUnconfirmedPayment = useCallback((rentalId: string) => {
    setUnconfirmedRentalId(rentalId)
    setIsUnconfirmedDialogVisible(true)
  }, [])

  /**
   * "El pago no fue procesado" cuando NO HUBO NINGUN PAGO.
   *
   * Desde el 2026-08-25 el servidor rechaza crear la reserva de quien todavia no
   * puede pagar (`private.assert_rental_insert_is_a_request`). Ese rechazo
   * llegaba a los tres `catch` de esta pantalla, y los tres hacian lo mismo:
   * toast de error con la descripcion `payment.failed_desc` --"El pago no fue
   * procesado. Intenta de nuevo o usa otro metodo de pago"-- mas una vibracion
   * de error y un reporte a Sentry.
   *
   * Las tres cosas son falsas. No se intento ningun cobro, no sirve otra
   * tarjeta, y no hay ninguna averia que investigar: es la regla del producto
   * funcionando. Es exactamente el mismo error que se arreglo en agosto en el
   * otro sentido --decirle a alguien que su TARJETA fue rechazada cuando lo que
   * pasaba es que nunca se habia verificado-- y habia vuelto por la puerta de
   * atras.
   *
   * Devuelve `true` cuando ya se ocupo del asunto, para que el `catch` que lo
   * llama se detenga ahi.
   */
  const handledBeforePaymentBookingBlock = useCallback(
    (err: unknown): boolean => {
      const code = (err as { code?: string })?.code

      if (code === 'BOOKING_VEHICLE_UNAVAILABLE') {
        toast.info(errorMessage(err, t('checkout.vehicle_no_longer_available')))
        // The trigger rejected the insert before a hold or payment existed.
        // Returning to live inventory prevents a stale checkout from looking
        // like a retryable card failure.
        router.replace('/(tabs)/explore' as never)
        return true
      }

      if (code === 'RENTAL_TERMS_UNAVAILABLE') {
        toast.error(t('rental_terms_flow.changed'))
        return true
      }
      if (code === 'RENTAL_TERMS_CHANGED') {
        setTermsAccepted(false)
        void refetchTerms()
        const rentalId =
          (err as { rentalId?: string })?.rentalId ?? recoveredBooking?.id ?? pendingRentalId
        if (rentalId) setStaleHoldId(rentalId)
        else toast.info(t('rental_terms_flow.changed'))
        return true
      }
      if (code === 'RENTAL_TERMS_NOT_ACCEPTED') {
        setTermsAccepted(false)
        void refetchTerms()
        toast.info(t('rental_terms_flow.changed'))
        return true
      }
      if (code === 'BOOKING_TOO_SOON') {
        toast.info(t('rental_terms_flow.minimum_lead'))
        return true
      }
      if (code === 'HOST_TERMS_NOT_CURRENT') {
        toast.info(t('rental_terms_flow.host_unavailable'))
        return true
      }

      if (code !== 'BOOKING_NOT_ELIGIBLE') return false

      toast.info(errorMessage(err, t('payment.booking_blocked_unknown')))
      // Vuelve al checkout, donde la reserva a medio armar sigue viva.
      router.push(`/profile/verification?from=${encodeURIComponent(pathname)}` as never)
      return true
    },
    [pathname, pendingRentalId, recoveredBooking?.id, refetchTerms, router, t],
  )

  const restartStaleHold = useCallback(async () => {
    if (!staleHoldId || isRestartingStaleHold) return
    setIsRestartingStaleHold(true)
    try {
      const released = await releaseUnpaidHold(staleHoldId)
      if (!released) throw new Error('Stale unpaid hold could not be safely released')
      setStaleHoldId(null)
      setPendingRentalId(null)
      setTermsAccepted(false)
      void refetchTerms()
      if (isRecoveredCheckout && activeVehicleId) {
        router.replace(`/explore/${activeVehicleId}` as never)
      } else {
        toast.info(t('rental_terms_flow.restart_ready'))
      }
    } catch (error) {
      captureError(error, { screen: 'booking/checkout', action: 'restart-stale-hold' })
      toast.error(t('rental_terms_flow.restart_failed'))
    } finally {
      setIsRestartingStaleHold(false)
    }
  }, [
    activeVehicleId,
    isRecoveredCheckout,
    isRestartingStaleHold,
    refetchTerms,
    releaseUnpaidHold,
    router,
    staleHoldId,
    t,
  ])

  /**
   * The one escape from an unconfirmed payment. Shared by the dialog's confirm
   * and by the persistent notice, so dismissing the dialog can never be the
   * move that removes the way out.
   */
  const goToTripsForUnconfirmedPayment = useCallback(() => {
    setIsUnconfirmedDialogVisible(false)
    allowLeaveWithoutRelease()
    router.replace('/(tabs)/trips')
  }, [allowLeaveWithoutRelease, router])

  // Create booking helper — reuses existing rental on retry (P1-5: idempotency)
  const ensureBooking = useCallback(async () => {
    if (!termsAccepted || !currentTerms || !profile?.id) {
      throw Object.assign(new Error('Rental terms must be reviewed'), {
        code: 'RENTAL_TERMS_UNAVAILABLE',
      })
    }
    try {
      await legalService.acceptCurrentRentalTerms(currentTerms.version)
    } catch (error) {
      setTermsAccepted(false)
      setTermsError(t('rental_terms_flow.changed'))
      void refetchTerms()
      throw Object.assign(new Error('Rental terms acceptance failed', { cause: error }), {
        code: 'RENTAL_TERMS_UNAVAILABLE',
      })
    }
    // The NCND row is an audit record, not the gate. It is written once the
    // booking is created and fails open: a hiccup persisting it must never take
    // a booking down. The rental-terms acceptance above is the contract between
    // guest and host, so that one stays blocking.
    void legalService.acceptTermsNcnd(profile.id).catch(() => {})
    const requireCurrentHold = (version: string | null, rentalId: string) => {
      if (version !== currentTerms.version) {
        throw Object.assign(new Error('Unpaid hold uses superseded rental terms'), {
          code: 'RENTAL_TERMS_CHANGED',
          rentalId,
        })
      }
    }
    if (recoveredBooking?.id) {
      requireCurrentHold(recoveredBooking.rentalTermsVersion, recoveredBooking.id)
      productTelemetryService.recordEvent('booking_step', 'booking.rental_reused', {
        elementId: 'checkout.resume',
        metadata: { source: 'recovered_checkout' },
      })
      return { id: recoveredBooking.id }
    }
    if (!activeVehicleId || !startDateTime || !endDateTime) throw new Error('Missing booking data')

    /*
     * REUSAR LA RESERVA DE ESTA SESION, PERO SOLO SI SIGUE VIVA.
     *
     * `expire_abandoned_payment_holds` corre cada 10 minutos y cancela toda
     * reserva sin pagar con mas de 30 minutos. Esta pantalla puede quedarse
     * abierta mucho mas que eso: le rebota la tarjeta, sale a buscar otra, y
     * vuelve a los 35 minutos.
     *
     * Antes se devolvia `pendingRentalId` sin mirar su estado, asi que a partir
     * de ahi TODO intento apuntaba a una reserva cancelada. `create-payment` la
     * rechaza, y la persona lee "Pago rechazado. Intenta de nuevo o usa otro
     * metodo de pago". Prueba una segunda tarjeta. Y una tercera. Sale creyendo
     * que su tarjeta no sirve.
     *
     * La comprobacion FALLA-ABIERTO: si no se puede leer el estado, se reusa
     * igual y decide el servidor. Romper el camino bueno por una consulta caida
     * seria peor que el defecto que esto arregla.
     *
     * Cuando esta muerta no hay nada especial que hacer: se descarta el id y se
     * sigue de largo a `createBooking`, que ya sabe buscar-o-crear la reserva de
     * esta persona para este carro y estas fechas (booking.service.ts:481).
     */
    if (pendingRentalId) {
      const pendingBooking = await bookingService
        .getBookingDetail(pendingRentalId)
        .catch(() => null)
      const sigueViva = pendingBooking?.status === 'pending_payment' || !pendingBooking

      if (sigueViva) {
        if (pendingBooking) requireCurrentHold(pendingBooking.rentalTermsVersion, pendingBooking.id)
        productTelemetryService.recordEvent('booking_step', 'booking.rental_reused', {
          elementId: 'checkout.payment',
          metadata: { source: 'pending_payment' },
        })
        return { id: pendingRentalId }
      }

      productTelemetryService.recordEvent('booking_step', 'booking.rental_expired_recreated', {
        elementId: 'checkout.payment',
        metadata: { source: 'swept_hold' },
      })
      setPendingRentalId(null)
    }
    productTelemetryService.recordEvent('booking_step', 'booking.rental_create_started', {
      elementId: 'checkout.payment',
      metadata: {
        durationHours: selectedDurationHours,
        minRentalHours: minRentalHours ?? 0,
        hasPromo: Boolean(appliedPromoCode),
        priceBucket: amountBucket(pricing?.totalCents),
      },
    })
    const booking = await createBooking({
      vehicleId: activeVehicleId,
      startDate: startDateTime,
      endDate: endDateTime,
      pickupAddress: effectivePickupLocation?.address ?? '',
      pickupLat: effectivePickupLocation?.lat ?? 0,
      pickupLng: effectivePickupLocation?.lng ?? 0,
      dropoffAddress: effectiveDropoffLocation?.address ?? effectivePickupLocation?.address ?? '',
      dropoffLat: effectiveDropoffLocation?.lat ?? effectivePickupLocation?.lat ?? 0,
      dropoffLng: effectiveDropoffLocation?.lng ?? effectivePickupLocation?.lng ?? 0,
      isSameLocation,
      insuranceTier,
      promoCode: appliedPromoCode ?? undefined,
    })
    requireCurrentHold(booking.rentalTermsVersion, booking.id)
    setPendingRentalId(booking.id)
    productTelemetryService.recordEvent('booking_step', 'booking.rental_create_succeeded', {
      elementId: 'checkout.payment',
      metadata: {
        durationHours: selectedDurationHours,
        minRentalHours: minRentalHours ?? 0,
        hasPromo: Boolean(appliedPromoCode),
        priceBucket: amountBucket(pricing?.totalCents),
      },
    })
    return booking
  }, [
    activeVehicleId,
    startDateTime,
    endDateTime,
    effectivePickupLocation,
    effectiveDropoffLocation,
    isSameLocation,
    insuranceTier,
    createBooking,
    appliedPromoCode,
    pendingRentalId,
    recoveredBooking,
    selectedDurationHours,
    minRentalHours,
    pricing?.totalCents,
    profile?.id,
    currentTerms,
    termsAccepted,
    refetchTerms,
    t,
  ])

  const handlePayPalPayment = useCallback(async () => {
    if (isSubmittingRef.current) return
    if (!hasCheckoutData) {
      productTelemetryService.recordEvent('payment_step', 'payment.paypal_blocked', {
        elementId: 'checkout.paypal',
        elementType: 'button',
        metadata: { reason: 'missing_checkout_data' },
      })
      toast.error(checkoutBlockedMessage)
      void notification(NotificationType.Error)
      return
    }
    if (!termsAccepted) {
      productTelemetryService.recordEvent('payment_step', 'payment.paypal_blocked', {
        elementId: 'checkout.paypal',
        elementType: 'button',
        metadata: { reason: 'terms_not_accepted' },
      })
      const message = t('checkout.accept_terms_error')
      setTermsError(message)
      toast.error(message)
      void notification(NotificationType.Error)
      return
    }

    isSubmittingRef.current = true
    productTelemetryService.recordEvent('payment_step', 'payment.paypal_started', {
      elementId: 'checkout.paypal',
      elementType: 'button',
      metadata: {
        recoveredCheckout: isRecoveredCheckout,
        priceBucket: amountBucket(pricing?.totalCents),
        appSwitch: Platform.OS === 'ios' || Platform.OS === 'android',
      },
    })

    try {
      const booking = await ensureBooking()
      const result = await processPayPalPayment({
        rentalId: booking.id,
        payer: checkoutPayerFromProfile(profile),
        appSwitch:
          Platform.OS === 'ios' || Platform.OS === 'android'
            ? { platform: Platform.OS, osVersion: String(Platform.Version) }
            : { platform: 'web' },
      })

      if (!result.challenge) {
        throw new Error(t('payment.hosted_checkout_could_not_start'))
      }
      setHostedCheckoutUrl(result.challenge.url)
      productTelemetryService.recordEvent('payment_step', 'payment.paypal_redirect_started', {
        elementId: 'checkout.paypal',
        elementType: 'button',
        metadata: { appSwitch: Platform.OS === 'ios' || Platform.OS === 'android' },
      })
      await Linking.openURL(result.challenge.url)
    } catch (err) {
      if (handledBeforePaymentBookingBlock(err)) return
      productTelemetryService.recordEvent('payment_step', 'payment.paypal_failed', {
        elementId: 'checkout.paypal',
        elementType: 'button',
        metadata: { source: 'client_exception' },
      })
      setThreeDsChallenge(null)
      setIsConfirmingPayment(false)
      if (__DEV__) console.warn('[Checkout] PayPal payment failed:', err)
      presentPaymentFailure(err, 'HOSTED_CHECKOUT_FAILED')
      void notification(NotificationType.Error)
      captureError(err, { screen: 'booking/checkout', action: 'paypal-payment' })
    } finally {
      isSubmittingRef.current = false
    }
  }, [
    checkoutBlockedMessage,
    ensureBooking,
    handledBeforePaymentBookingBlock,
    hasCheckoutData,
    isRecoveredCheckout,
    pricing?.totalCents,
    processPayPalPayment,
    profile,
    t,
    termsAccepted,
    presentPaymentFailure,
  ])

  const handleCardPayment = useCallback(
    async (card: CardPaymentData) => {
      if (isSubmittingRef.current) return
      if (!hasCheckoutData) {
        productTelemetryService.recordEvent('payment_step', 'payment.new_card_blocked', {
          elementId: 'checkout.card-form',
          elementType: 'form',
          metadata: { reason: 'missing_checkout_data' },
        })
        toast.error(checkoutBlockedMessage)
        void notification(NotificationType.Error)
        return
      }
      if (!termsAccepted) {
        productTelemetryService.recordEvent('payment_step', 'payment.new_card_blocked', {
          elementId: 'checkout.card-form',
          elementType: 'form',
          metadata: { reason: 'terms_not_accepted' },
        })
        const message = t('checkout.accept_terms_error')
        setTermsError(message)
        toast.error(message)
        void notification(NotificationType.Error)
        return
      }

      isSubmittingRef.current = true
      setPaymentOutcomeCode(null)
      productTelemetryService.recordEvent('payment_step', 'payment.new_card_started', {
        elementId: 'checkout.card-form',
        elementType: 'form',
        metadata: {
          recoveredCheckout: isRecoveredCheckout,
          hasSavedCards: savedCardsAvailableForCheckout.length > 0,
          priceBucket: amountBucket(pricing?.totalCents),
        },
      })
      let failureRecorded = false
      let rentalIdForRelease: string | null = pendingRentalId
      try {
        const booking = await ensureBooking()
        rentalIdForRelease = booking.id
        const profileFullName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim()
        const fullName = (card.cardholderName ?? profileFullName).trim()
        const nameParts = fullName.split(/\s+/).filter(Boolean)
        const firstName = nameParts[0] ?? profile?.firstName ?? 'Cliente'
        const lastName = nameParts.slice(1).join(' ') || profile?.lastName || 'Tago'

        const result = await processCardPayment({
          rentalId: booking.id,
          card,
          payer: checkoutPayerFromProfile(profile, { firstName, lastName }),
        })

        if (result.challenge) {
          productTelemetryService.recordEvent('payment_step', 'payment.3ds_required', {
            elementId: 'checkout.card-form',
            elementType: 'form',
            metadata: { method: 'new_card', kind: result.challenge.kind },
          })
          setThreeDsChallenge({
            url: result.challenge.url,
            rentalId: booking.id,
            transactionId: result.transactionId,
          })
          return
        }

        if (result.status === 'captured') {
          productTelemetryService.recordEvent('payment_step', 'payment.captured', {
            elementId: 'checkout.card-form',
            elementType: 'form',
            metadata: { method: 'new_card', source: 'direct_response' },
          })
          void notification(NotificationType.Success)
          allowLeaveWithoutRelease()
          router.replace(`/booking/confirmation?rentalId=${booking.id}` as never)
          return
        }

        if (result.status === 'failed') {
          productTelemetryService.recordEvent('payment_step', 'payment.failed', {
            elementId: 'checkout.card-form',
            elementType: 'form',
            metadata: { method: 'new_card', source: 'direct_response' },
          })
          failureRecorded = true
          throw new Error(t('payment.failed'))
        }

        setIsConfirmingPayment(true)
        let confirmation: ConfirmResult
        try {
          confirmation = await confirmProviderPayment({
            rentalId: booking.id,
            transactionId: result.transactionId,
          })
        } finally {
          setIsConfirmingPayment(false)
        }
        if (confirmation.status === 'captured') {
          productTelemetryService.recordEvent('payment_step', 'payment.captured', {
            elementId: 'checkout.card-form',
            elementType: 'form',
            metadata: { method: 'new_card', source: 'provider_confirm' },
          })
          void notification(NotificationType.Success)
          allowLeaveWithoutRelease()
          router.replace(`/booking/confirmation?rentalId=${booking.id}` as never)
          return
        }

        if (confirmation.status === 'refunded' || confirmation.status === 'refund_pending') {
          productTelemetryService.recordEvent('payment_step', 'payment.refunded', {
            elementId: 'checkout.card-form',
            elementType: 'form',
            metadata: { source: 'provider_confirm', settled: confirmation.status === 'refunded' },
          })
          void notification(NotificationType.Warning)
          setRefundedPayment({ settled: confirmation.status === 'refunded' })
          setIsRefundDialogVisible(true)
          return
        }

        if (confirmation.status === 'failed') {
          productTelemetryService.recordEvent('payment_step', 'payment.failed', {
            elementId: 'checkout.card-form',
            elementType: 'form',
            metadata: { method: 'new_card', source: 'provider_confirm' },
          })
          failureRecorded = true
          throw new Error(confirmation.reason || t('payment.failed'))
        }

        // status === null: eight polls and the provider still has not said
        // yes or no. Reporting a decline here would be a lie that costs the
        // guest a second charge — own the uncertainty instead.
        productTelemetryService.recordEvent('payment_step', 'payment.unconfirmed', {
          elementId: 'checkout.card-form',
          elementType: 'form',
          metadata: { method: 'new_card', source: 'provider_timeout' },
        })
        void notification(NotificationType.Warning)
        showUnconfirmedPayment(booking.id)
      } catch (err) {
        if (handledBeforePaymentBookingBlock(err)) return
        if (!failureRecorded) {
          productTelemetryService.recordEvent('payment_step', 'payment.failed', {
            elementId: 'checkout.card-form',
            elementType: 'form',
            metadata: { method: 'new_card', source: 'client_exception' },
          })
        }
        setThreeDsChallenge(null)
        setIsConfirmingPayment(false)
        await freeUnpaidHold(rentalIdForRelease)
        if (__DEV__) console.warn('[Checkout] card payment failed:', err)
        presentPaymentFailure(err, 'DECLINED')
        void notification(NotificationType.Error)
        captureError(err, { screen: 'booking/checkout', action: 'card-3ds-payment' })
      } finally {
        isSubmittingRef.current = false
      }
    },
    [
      handledBeforePaymentBookingBlock,
      hasCheckoutData,
      checkoutBlockedMessage,
      termsAccepted,
      ensureBooking,
      profile,
      processCardPayment,
      router,
      showUnconfirmedPayment,
      t,
      isRecoveredCheckout,
      pricing?.totalCents,
      savedCardsAvailableForCheckout.length,
      freeUnpaidHold,
      pendingRentalId,
      presentPaymentFailure,
      allowLeaveWithoutRelease,
    ],
  )

  const handleSavedCardPayment = useCallback(async () => {
    if (isSubmittingRef.current) return
    if (!selectedPaymentMethod) return
    if (!hasCheckoutData) {
      productTelemetryService.recordEvent('payment_step', 'payment.saved_card_blocked', {
        elementId: 'checkout.pay-saved-card',
        elementType: 'button',
        metadata: { reason: 'missing_checkout_data' },
      })
      toast.error(checkoutBlockedMessage)
      void notification(NotificationType.Error)
      return
    }
    if (!termsAccepted) {
      productTelemetryService.recordEvent('payment_step', 'payment.saved_card_blocked', {
        elementId: 'checkout.pay-saved-card',
        elementType: 'button',
        metadata: { reason: 'terms_not_accepted' },
      })
      const message = t('checkout.accept_terms_error')
      setTermsError(message)
      toast.error(message)
      void notification(NotificationType.Error)
      return
    }

    const expectedLength = expectedCvvLength(selectedPaymentMethod)
    const cvv = savedCardCvv.replace(/\D/g, '')
    if (cvv.length !== expectedLength) {
      productTelemetryService.recordEvent('payment_step', 'payment.saved_card_blocked', {
        elementId: 'checkout.pay-saved-card',
        elementType: 'button',
        metadata: { reason: 'invalid_cvv_length' },
      })
      toast.error(t('card_form.invalid_cvv'))
      void notification(NotificationType.Error)
      return
    }

    isSubmittingRef.current = true
    setPaymentOutcomeCode(null)
    productTelemetryService.recordEvent('payment_step', 'payment.saved_card_started', {
      elementId: 'checkout.pay-saved-card',
      elementType: 'button',
      metadata: {
        cardBrand: selectedPaymentMethod.cardBrand ?? 'unknown',
        recoveredCheckout: isRecoveredCheckout,
        priceBucket: amountBucket(pricing?.totalCents),
      },
    })
    let failureRecorded = false
    try {
      const booking = await ensureBooking()
      const firstName = profile?.firstName || 'Cliente'
      const lastName = profile?.lastName || 'Tago'
      const result = await processCardPayment({
        rentalId: booking.id,
        paymentMethodId: selectedPaymentMethod.id,
        cvv,
        payer: checkoutPayerFromProfile(profile, { firstName, lastName }),
      })

      if (result.status === 'captured') {
        productTelemetryService.recordEvent('payment_step', 'payment.captured', {
          elementId: 'checkout.pay-saved-card',
          elementType: 'button',
          metadata: { method: 'saved_card', source: 'direct_response' },
        })
        void notification(NotificationType.Success)
        allowLeaveWithoutRelease()
        router.replace(`/booking/confirmation?rentalId=${booking.id}` as never)
        return
      }

      if (result.status === 'failed') {
        productTelemetryService.recordEvent('payment_step', 'payment.failed', {
          elementId: 'checkout.pay-saved-card',
          elementType: 'button',
          metadata: { method: 'saved_card', source: 'direct_response' },
        })
        failureRecorded = true
        throw new Error(t('payment.failed'))
      }

      if (result.challenge) {
        productTelemetryService.recordEvent('payment_step', 'payment.3ds_required', {
          elementId: 'checkout.pay-saved-card',
          elementType: 'button',
          metadata: { method: 'saved_card', kind: result.challenge.kind },
        })
        setThreeDsChallenge({
          url: result.challenge.url,
          rentalId: booking.id,
          transactionId: result.transactionId,
        })
        return
      }

      setIsConfirmingPayment(true)
      let confirmation: ConfirmResult
      try {
        confirmation = await confirmProviderPayment({
          rentalId: booking.id,
          transactionId: result.transactionId,
        })
      } finally {
        setIsConfirmingPayment(false)
      }
      if (confirmation.status === 'captured') {
        productTelemetryService.recordEvent('payment_step', 'payment.captured', {
          elementId: 'checkout.pay-saved-card',
          elementType: 'button',
          metadata: { method: 'saved_card', source: 'provider_confirm' },
        })
        void notification(NotificationType.Success)
        allowLeaveWithoutRelease()
        router.replace(`/booking/confirmation?rentalId=${booking.id}` as never)
        return
      }

      if (confirmation.status === 'refunded' || confirmation.status === 'refund_pending') {
        productTelemetryService.recordEvent('payment_step', 'payment.refunded', {
          elementId: 'checkout.card-form',
          elementType: 'form',
          metadata: { source: 'provider_confirm', settled: confirmation.status === 'refunded' },
        })
        void notification(NotificationType.Warning)
        setRefundedPayment({ settled: confirmation.status === 'refunded' })
        setIsRefundDialogVisible(true)
        return
      }

      if (confirmation.status === 'failed') {
        productTelemetryService.recordEvent('payment_step', 'payment.failed', {
          elementId: 'checkout.pay-saved-card',
          elementType: 'button',
          metadata: { method: 'saved_card', source: 'provider_confirm' },
        })
        failureRecorded = true
        throw new Error(confirmation.reason || t('payment.failed'))
      }

      // status === null — unknown, not declined. See handleCardPayment.
      productTelemetryService.recordEvent('payment_step', 'payment.unconfirmed', {
        elementId: 'checkout.pay-saved-card',
        elementType: 'button',
        metadata: { method: 'saved_card', source: 'provider_timeout' },
      })
      void notification(NotificationType.Warning)
      showUnconfirmedPayment(booking.id)
    } catch (err) {
      if (handledBeforePaymentBookingBlock(err)) return
      if (!failureRecorded) {
        productTelemetryService.recordEvent('payment_step', 'payment.failed', {
          elementId: 'checkout.pay-saved-card',
          elementType: 'button',
          metadata: { method: 'saved_card', source: 'client_exception' },
        })
      }
      setThreeDsChallenge(null)
      setIsConfirmingPayment(false)
      if (__DEV__) console.warn('[Checkout] saved card payment failed:', err)
      presentPaymentFailure(err, 'DECLINED')
      void notification(NotificationType.Error)
      captureError(err, { screen: 'booking/checkout', action: 'saved-card-payment' })
    } finally {
      isSubmittingRef.current = false
    }
  }, [
    handledBeforePaymentBookingBlock,
    selectedPaymentMethod,
    hasCheckoutData,
    checkoutBlockedMessage,
    termsAccepted,
    savedCardCvv,
    t,
    ensureBooking,
    profile,
    processCardPayment,
    router,
    showUnconfirmedPayment,
    isRecoveredCheckout,
    pricing?.totalCents,
    presentPaymentFailure,
    allowLeaveWithoutRelease,
  ])

  const handleThreeDsComplete = useCallback(async () => {
    if (!threeDsChallenge) return

    try {
      productTelemetryService.recordEvent('payment_step', 'payment.3ds_complete_started', {
        elementId: 'checkout.3ds-modal',
        elementType: 'modal',
      })
      setIsConfirmingPayment(true)
      let confirmation: ConfirmResult
      try {
        confirmation = await confirmProviderPayment({
          rentalId: threeDsChallenge.rentalId,
          transactionId: threeDsChallenge.transactionId,
        })
      } finally {
        setIsConfirmingPayment(false)
      }
      setThreeDsChallenge(null)

      if (confirmation.status === 'captured') {
        productTelemetryService.recordEvent('payment_step', 'payment.captured', {
          elementId: 'checkout.3ds-modal',
          elementType: 'modal',
          metadata: { source: '3ds_complete' },
        })
        void notification(NotificationType.Success)
        allowLeaveWithoutRelease()
        router.replace(`/booking/confirmation?rentalId=${threeDsChallenge.rentalId}` as never)
        return
      }

      if (confirmation.status === 'refunded' || confirmation.status === 'refund_pending') {
        productTelemetryService.recordEvent('payment_step', 'payment.refunded', {
          elementId: 'checkout.card-form',
          elementType: 'form',
          metadata: { source: 'provider_confirm', settled: confirmation.status === 'refunded' },
        })
        void notification(NotificationType.Warning)
        setRefundedPayment({ settled: confirmation.status === 'refunded' })
        setIsRefundDialogVisible(true)
        return
      }

      if (confirmation.status === 'failed') {
        productTelemetryService.recordEvent('payment_step', 'payment.failed', {
          elementId: 'checkout.3ds-modal',
          elementType: 'modal',
          metadata: { source: '3ds_complete' },
        })
        await freeUnpaidHold(threeDsChallenge.rentalId)
        presentPaymentFailure(
          {
            paymentCode: classifyUnknownPaymentFailure({ message: confirmation.reason }).code,
            message: confirmation.reason,
          },
          'DECLINED',
        )
        void notification(NotificationType.Error)
        return
      }

      // Unknown, not declined — same honest treatment as the direct card path.
      productTelemetryService.recordEvent('payment_step', 'payment.unconfirmed', {
        elementId: 'checkout.3ds-modal',
        elementType: 'modal',
        metadata: { source: '3ds_timeout' },
      })
      void notification(NotificationType.Warning)
      showUnconfirmedPayment(threeDsChallenge.rentalId)
    } catch (err) {
      if (handledBeforePaymentBookingBlock(err)) return
      productTelemetryService.recordEvent('payment_step', 'payment.failed', {
        elementId: 'checkout.3ds-modal',
        elementType: 'modal',
        metadata: { source: '3ds_exception' },
      })
      setThreeDsChallenge(null)
      await freeUnpaidHold(threeDsChallenge.rentalId)
      presentPaymentFailure(err, 'DECLINED')
      void notification(NotificationType.Error)
      captureError(err, { screen: 'booking/checkout', action: 'confirm-card-3ds' })
    }
  }, [
    freeUnpaidHold,
    handledBeforePaymentBookingBlock,
    router,
    showUnconfirmedPayment,
    threeDsChallenge,
    presentPaymentFailure,
    allowLeaveWithoutRelease,
  ])

  const handleThreeDsCancel = useCallback(() => {
    productTelemetryService.recordEvent('payment_step', 'payment.3ds_cancelled', {
      elementId: 'checkout.3ds-modal',
      elementType: 'modal',
    })
    const rentalId = threeDsChallenge?.rentalId
    setThreeDsChallenge(null)
    void freeUnpaidHold(rentalId)
    setPaymentOutcomeCode('CHALLENGE_ABANDONED')
  }, [freeUnpaidHold, threeDsChallenge])

  const primaryImage = vehicle?.images.find((img) => img.isPrimary) ?? vehicle?.images[0]
  const checkoutGallery = (vehicle?.images ?? []).filter(
    (img) => !isInspectionPhotoRequirement(img.requirementId),
  )
  const checkoutLightboxImages = (
    checkoutGallery.length > 0 ? checkoutGallery : (vehicle?.images ?? [])
  ).map((img) => ({
    id: img.id,
    uri: img.url,
    blurhash: img.blurhash,
    accessibilityLabel: vehicle?.title,
  }))
  const openCheckoutLightbox = () => {
    if (checkoutLightboxImages.length === 0) return
    const start = primaryImage
      ? checkoutLightboxImages.findIndex((img) => img.id === primaryImage.id)
      : 0
    lightbox.open(checkoutLightboxImages, start < 0 ? 0 : start)
  }
  const checkoutPadX = isEditorial ? SPACING.lg : SPACING.md
  const checkoutHorizontalMargin = isEditorial ? 0 : SPACING.md
  const tripRangeLabel =
    displayStartDate && displayEndDate
      ? formatTripRangeLabel({
          startDate: displayStartDate,
          endDate: displayEndDate,
          startTime: displayStartTime,
          endTime: displayEndTime,
        })
      : null
  const handleCheckoutScroll = useProductTelemetryScroll('checkout.screen.scroll')

  const handleSaveLocation = (location: BookingLocationValue) => {
    if (locationPickerMode === 'pickup') {
      setPickup(location.address, location.lat, location.lng)
      if (isSameLocation) setDropoff(location.address, location.lat, location.lng)
    } else if (locationPickerMode === 'dropoff') {
      setDropoff(location.address, location.lat, location.lng)
      setSameLocation(false)
    }
    setLocationPickerMode(null)
  }

  // AML gate: toast + redirect already fired in useAmlGate. Themed fallback
  // rather than `null`, which paints a full-screen white blank while the
  // redirect is in flight — see components/ui/GateFallback.tsx.
  if (amlGated) {
    return <GateFallback accessibilityLabel={t('verification.gate_redirecting')} />
  }

  return (
    <ScreenContainer variant="full-bleed" card={false} scroll={false}>
      <View style={{ flex: 1 }}>
        {/* Loading overlay — in-flight ONLY. This scrim is painted over the
            ScreenHeader below it, so its back button is underneath it; a scrim
            that outlives the request it belongs to is a lid on the money
            screen. It must never be driven by a terminal state. */}
        {isPaymentInFlight && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: colors.scrim,
              zIndex: Z_INDEX.overlay,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <View
              style={{
                backgroundColor: colors.surface,
                padding: SPACING.lg,
                borderRadius: RADIUS.xl,
                alignItems: 'center',
                gap: SPACING.md,
              }}
            >
              <AnimatedSpinner size="lg" />
              <Text style={{ fontSize: 16, fontFamily: FONTS.body, color: colors.textPrimary }}>
                {t('checkout.processing')}
              </Text>
            </View>
          </View>
        )}

        <KeyboardAwareView
          includeTopInset={false}
          style={{ flex: 1, width: '100%' }}
          contentContainerStyle={{
            paddingHorizontal: isEditorial ? 0 : SPACING.lg,
            paddingTop: 0,
            paddingBottom: isEditorial
              ? insets.bottom + SPACING['2xl']
              : insets.bottom + SPACING.lg + 80,
          }}
          keyboardShouldPersistTaps="handled"
          // EN iOS EL TECLADO TAPABA EL BOTON DE PAGAR, y no habia forma de bajarlo.
          //
          // OJO, LA CLASE SIGUE ABIERTA. La primera version de este comentario
          // decia que esta era "la unica pantalla con formulario sin manejo de
          // teclado". Es falso: barriendo las 30 pantallas con TextInput quedan
          // 13 mas sin nada --new-listing/{pricing,location,documents},
          // organization/{members,settings}, profile/delete-account,
          // verification/license, booking/cancel, booking/dispute/{file,respond},
          // booking/review, y las dos de checkin--. Un comentario que declara
          // cerrada una clase garantiza que nadie vuelva a mirar; queda dicho
          // que faltan. El otro caso grave, la hoja de agregar tarjeta que monta
          // este MISMO CardForm, se arreglo en el mismo commit
          // (profile/payment-methods.tsx).
          //
          // En Android nunca se noto porque el prebuild pone `adjustResize` y la
          // ventana se encoge sola; en iOS el default de RN es NO ajustar --Fabric:
          // RCTScrollViewComponentView.mm, `_automaticallyAdjustKeyboardInsets = NO`--
          // asi que el scroll no ganaba un pixel. El formulario de tarjeta es el
          // ULTIMO bloque y el boton de pagar vive dentro de el, con un colchon de
          // insets.bottom + 24 + 80 (~138pt) contra un teclado de ~250-290pt: el
          // boton quedaba enterrado y no habia scroll que lo rescatara.
          //
          // Y no se podia salir del teclado: los tres campos son `number-pad`, que
          // en iOS no dibuja tecla de retorno, asi que `returnKeyType` es inerte.
          //
          // `automaticallyAdjustKeyboardInsets` es la contraparte de `adjustResize`
          // y es lo que RN expone para esto. `keyboardDismissMode` da la salida que
          // el number pad no tiene: se arrastra hacia abajo y el teclado se va.
          automaticallyAdjustKeyboardInsets
          keyboardDismissMode="interactive"
          showsVerticalScrollIndicator={false}
          onScroll={handleCheckoutScroll}
          scrollEventThrottle={64}
        >
          <ScreenHeader
            title={t('checkout.title')}
            backTestID="checkout.back-button"
            style={{
              paddingHorizontal: checkoutPadX,
              paddingTop: SPACING.sm,
              paddingBottom: SPACING.sm,
            }}
          />

          <CheckoutEditorialRow
            editorial={isEditorial}
            padX={checkoutPadX}
            testID="checkout.editorial-layout"
          >
            <CheckoutSummaryColumn editorial={isEditorial}>
              {/* Vehicle card — editorial is a cover photo, phone stays a compact row. */}
              {vehicle && isEditorial ? (
                <CheckoutVehicleHero
                  title={vehicle.title}
                  dateLabel={tripRangeLabel}
                  imageUrl={primaryImage?.url}
                  blurhash={primaryImage?.blurhash}
                  eyebrow={t('checkout.your_trip')}
                  onPhotoPress={primaryImage ? openCheckoutLightbox : undefined}
                />
              ) : vehicle ? (
                <View
                  style={{
                    marginHorizontal: checkoutHorizontalMargin,
                    padding: SPACING.md,
                    backgroundColor: colors.surface,
                    borderRadius: RADIUS.lg,
                    borderWidth: 1,
                    borderColor: colors.border,
                    flexDirection: 'row',
                    gap: SPACING.md,
                  }}
                >
                  {primaryImage && (
                    <Pressable
                      onPress={openCheckoutLightbox}
                      accessibilityRole="button"
                      accessibilityLabel={t('vehicle_detail.view_image')}
                      testID="checkout.vehicle-photo"
                    >
                      <Image
                        source={primaryImage.url}
                        blurhash={primaryImage.blurhash ?? undefined}
                        width={80}
                        height={60}
                        contentFit="cover"
                        borderRadius={RADIUS.sm}
                        accessibilityLabel={vehicle?.title ?? ''}
                      />
                    </Pressable>
                  )}
                  <View style={{ flex: 1, justifyContent: 'center' }}>
                    <Text
                      style={{
                        fontSize: 17,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                      numberOfLines={1}
                    >
                      {vehicle.title}
                    </Text>
                    {tripRangeLabel ? (
                      <Text
                        style={{
                          fontSize: 13,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginTop: SPACING.xs,
                        }}
                      >
                        {tripRangeLabel}
                      </Text>
                    ) : null}
                  </View>
                </View>
              ) : null}

              {/* The step that is actually missing, named, with the way to go and do it. It sits
              above the card form because the card was never the problem. */}
              {complianceBlocked && complianceBlockedMessage ? (
                <CheckoutNotice
                  title={t('payment.booking_blocked_title')}
                  body={complianceBlockedMessage}
                  tone="warning"
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                  actionLabel={t('payment.booking_blocked_cta')}
                  actionTestID="checkout.compliance-cta"
                  onAction={() => {
                    // The hub, not a deep link into one step: several of these are usually missing
                    // at once, and dropping someone into the third of six is its own dead end.
                    markBookingComplianceDetour()
                    router.push('/(tabs)/profile/verification' as never)
                  }}
                />
              ) : null}

              {recoveryRentalId &&
              (isRecoveringBooking ||
                isRecoveryError ||
                (recoveredBooking && !recoveredRentalPayable)) ? (
                <CheckoutNotice
                  title={
                    isRecoveringBooking
                      ? t('checkout.resume_loading_title')
                      : isRecoveryError
                        ? t('checkout.resume_error_title')
                        : t('checkout.resume_already_handled_title')
                  }
                  body={checkoutBlockedMessage}
                  tone={isRecoveryError ? 'error' : 'warning'}
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                />
              ) : null}

              {/* The dialog's "Cerrar" hides the dialog and nothing else, and
              nothing re-opens it. Without this action the only route out of an
              unconfirmed payment lived on a dismissed one-shot dialog. */}
              {/* Charged, then reversed because the booking was already gone. The
              guest must be told what happened to their money — the previous
              behaviour was the confirmation screen, which claimed a trip that
              does not exist. */}
              {refundedPayment && (
                <CheckoutNotice
                  title={t('payment.refunded_title')}
                  body={
                    refundedPayment.settled
                      ? t('payment.refunded_desc')
                      : t('payment.refund_pending_desc')
                  }
                  tone="warning"
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                  actionLabel={t('payment.unconfirmed_action')}
                  onAction={goToTripsForUnconfirmedPayment}
                  actionTestID="checkout.payment-refunded-notice-action"
                />
              )}

              {startIsPast ? (
                <CheckoutNotice
                  title={t('payment.start_in_past_title')}
                  body={t('checkout.start_in_the_past')}
                  tone="warning"
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                  testID="checkout.outcome.start-in-past"
                />
              ) : null}

              {startsTooSoon && !startIsPast ? (
                <CheckoutNotice
                  title={t('rental_terms_flow.minimum_lead_title')}
                  body={t('rental_terms_flow.minimum_lead')}
                  tone="warning"
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                  testID="checkout.outcome.host-response-lead"
                />
              ) : null}

              {paymentOutcomeCode ? (
                <CheckoutNotice
                  title={t(paymentOutcomePresentation(paymentOutcomeCode).titleKey)}
                  body={t(paymentOutcomePresentation(paymentOutcomeCode).bodyKey)}
                  tone={paymentOutcomePresentation(paymentOutcomeCode).tone}
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                  testID={paymentOutcomePresentation(paymentOutcomeCode).testID}
                  actionLabel={
                    paymentOutcomePresentation(paymentOutcomeCode).retryable
                      ? t('payment.retry')
                      : undefined
                  }
                  onAction={
                    paymentOutcomePresentation(paymentOutcomeCode).retryable
                      ? () => setPaymentOutcomeCode(null)
                      : undefined
                  }
                  actionTestID="checkout.outcome.retry"
                />
              ) : null}

              {unconfirmedRentalId ? (
                <CheckoutNotice
                  title={t('payment.unconfirmed_title')}
                  body={t('payment.unconfirmed_desc')}
                  tone="warning"
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                  actionLabel={t('payment.unconfirmed_action')}
                  onAction={goToTripsForUnconfirmedPayment}
                  actionTestID="checkout.payment-unconfirmed-notice-action"
                />
              ) : null}

              {vehicle && !hasVehicleHomeLocation && (
                <View
                  style={{
                    marginTop: SPACING.md,
                    marginHorizontal: checkoutHorizontalMargin,
                    padding: SPACING.md,
                    borderRadius: RADIUS.lg,
                    borderWidth: 1,
                    borderColor: colors.warning,
                    backgroundColor: `${colors.warning}12`,
                    flexDirection: 'row',
                    gap: SPACING.sm,
                    alignItems: 'flex-start',
                  }}
                >
                  <AlertCircle size={19} color={colors.warning} strokeWidth={2} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={{
                        fontSize: 14,
                        lineHeight: 19,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {t('checkout.vehicle_location_missing_title')}
                    </Text>
                    <Text
                      style={{
                        marginTop: 2,
                        fontSize: 13,
                        lineHeight: 18,
                        fontFamily: FONTS.body,
                        color: colors.textSecondary,
                      }}
                    >
                      {t('checkout.vehicle_location_missing')}
                    </Text>
                  </View>
                </View>
              )}

              {vehicle &&
                hasVehicleHomeLocation &&
                effectivePickupLocation &&
                effectiveDropoffLocation && (
                  <View
                    style={{
                      marginTop: SPACING.md,
                      marginHorizontal: checkoutHorizontalMargin,
                      padding: SPACING.md,
                      backgroundColor: colors.surface,
                      borderRadius: RADIUS.lg,
                      borderWidth: 1,
                      borderColor: colors.border,
                      gap: SPACING.sm,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 18,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {t('checkout.delivery_return_title')}
                    </Text>
                    <CheckoutLocationRow
                      label={t('checkout.pickup_at')}
                      value={effectivePickupLocation.address}
                      colors={colors}
                      onPress={
                        isRecoveredCheckout ? undefined : () => setLocationPickerMode('pickup')
                      }
                    />
                    <CheckoutLocationRow
                      label={t('checkout.dropoff_at')}
                      value={effectiveDropoffLocation.address}
                      colors={colors}
                      onPress={
                        isRecoveredCheckout ? undefined : () => setLocationPickerMode('dropoff')
                      }
                    />
                    {!isRecoveredCheckout && (
                      <Pressable
                        onPress={() => {
                          const next = !isSameLocation
                          setSameLocation(next)
                          if (next) {
                            setDropoff(
                              effectivePickupLocation.address,
                              effectivePickupLocation.lat,
                              effectivePickupLocation.lng,
                            )
                          }
                        }}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isSameLocation }}
                        testID="checkout.same-dropoff-location"
                        style={{
                          minHeight: INTERACTION.minTouchTarget,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: SPACING.sm,
                        }}
                      >
                        <CheckCircle
                          size={18}
                          color={isSameLocation ? COLORS.coral.DEFAULT : colors.textMuted}
                          strokeWidth={2}
                        />
                        <Text
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 13,
                            lineHeight: 18,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                          }}
                        >
                          {t('checkout.same_return_location')}
                        </Text>
                      </Pressable>
                    )}
                    <Text
                      style={{
                        fontSize: 13,
                        lineHeight: 18,
                        fontFamily: FONTS.body,
                        color: locationsWithinRadius ? colors.textSecondary : colors.error,
                      }}
                    >
                      {isRecoveredCheckout
                        ? t('checkout.resume_saved_locations')
                        : locationsWithinRadius
                          ? t('checkout.delivery_radius_notice', { radius: deliveryRadiusKm })
                          : t('checkout.location_out_of_radius')}
                    </Text>
                  </View>
                )}

              {/* Price breakdown */}
              {pricing && (
                <View
                  style={{
                    marginTop: SPACING.md,
                    marginHorizontal: checkoutHorizontalMargin,
                    padding: SPACING.md,
                    backgroundColor: colors.surface,
                    borderRadius: RADIUS.lg,
                    borderWidth: 1,
                    borderColor: colors.border,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 18,
                      fontFamily: FONTS.bodyBold,
                      color: colors.textPrimary,
                      marginBottom: SPACING.md,
                    }}
                  >
                    {t('checkout.summary')}
                  </Text>

                  {/* Promo code input */}
                  {!isRecoveredCheckout && !promoApplied ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: SPACING.sm,
                        marginBottom: SPACING.md,
                      }}
                    >
                      <View
                        style={{
                          flex: 1,
                          flexDirection: 'row',
                          alignItems: 'center',
                          height: 44,
                          borderWidth: 1,
                          borderColor: promoError ? colors.error : colors.border,
                          borderRadius: RADIUS.md,
                          paddingHorizontal: SPACING.sm,
                          backgroundColor: isDark ? colors.surfaceRaised : colors.surface,
                        }}
                      >
                        <Tag size={16} color={colors.textMuted} strokeWidth={1.5} />
                        <RNTextInput
                          value={promoInput}
                          onChangeText={(v) => {
                            setPromoInput(v.toUpperCase())
                            setPromoError(null)
                          }}
                          placeholder={t('booking.promo_code')}
                          placeholderTextColor={colors.textMuted}
                          autoCapitalize="characters"
                          autoCorrect={false}
                          style={{
                            flex: 1,
                            fontSize: 14,
                            fontFamily: FONTS.body,
                            color: colors.textPrimary,
                            paddingHorizontal: SPACING.sm,
                            height: 44,
                          }}
                          testID="checkout.promo-input"
                        />
                      </View>
                      <Pressable
                        onPress={async () => {
                          if (!promoInput.trim() || !profile?.id) return
                          void selection()
                          const normalizedPromo = promoInput.trim().toUpperCase()
                          if (normalizedPromo === 'WELCOME' && profileCompletion.percent < 100) {
                            setPromoApplied(false)
                            setPromoDiscountCents(0)
                            setPromoError(t('booking.promo_profile_incomplete'))
                            useBookingStore.getState().setPromoCode(null)
                            void notification(NotificationType.Error)
                            return
                          }
                          try {
                            const result = await validatePromo.mutateAsync({
                              code: normalizedPromo,
                              userId: profile.id,
                              subtotalCents: pricing.subtotalCents,
                            })
                            if (result.valid && result.discountCents) {
                              setPromoApplied(true)
                              setPromoInput(normalizedPromo)
                              setPromoDiscountCents(result.discountCents)
                              useBookingStore.getState().setPromoCode(normalizedPromo)
                              void notification(NotificationType.Success)
                            } else {
                              setPromoDiscountCents(0)
                              setPromoError(
                                t(`booking.promo_${result.reason?.toLowerCase() ?? 'invalid'}`),
                              )
                              void notification(NotificationType.Error)
                            }
                          } catch {
                            setPromoDiscountCents(0)
                            setPromoError(t('booking.promo_invalid'))
                            void notification(NotificationType.Error)
                          }
                        }}
                        disabled={validatePromo.isPending || !promoInput.trim()}
                        accessibilityRole="button"
                        accessibilityLabel={t('booking.apply_promo')}
                        testID="checkout.promo-apply"
                        style={{
                          height: 44,
                          paddingHorizontal: SPACING.md,
                          borderRadius: RADIUS.md,
                          backgroundColor: COLORS.coral.dark,
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: !promoInput.trim() || validatePromo.isPending ? 0.5 : 1,
                        }}
                      >
                        {validatePromo.isPending ? (
                          <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                          <Text
                            style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: '#FFFFFF' }}
                            numberOfLines={1}
                          >
                            {t('booking.apply_promo')}
                          </Text>
                        )}
                      </Pressable>
                    </View>
                  ) : !isRecoveredCheckout ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: SPACING.sm,
                        marginBottom: SPACING.md,
                        padding: SPACING.sm,
                        borderRadius: RADIUS.md,
                        backgroundColor: `${colors.success}12`,
                      }}
                    >
                      <CheckCircle size={16} color={colors.success} strokeWidth={2} />
                      <Text
                        style={{
                          flex: 1,
                          fontSize: 14,
                          fontFamily: FONTS.body,
                          color: colors.success,
                        }}
                      >
                        {t('booking.promo_applied')} - {promoInput}
                      </Text>
                      <Pressable
                        onPress={() => {
                          setPromoApplied(false)
                          setPromoInput('')
                          setPromoDiscountCents(0)
                          useBookingStore.getState().setPromoCode(null)
                          void selection()
                        }}
                        accessibilityRole="button"
                        accessibilityLabel={t('common.cancel')}
                        style={{ padding: SPACING.xs }}
                        testID="checkout.promo-remove"
                      >
                        <X size={16} color={colors.textMuted} strokeWidth={2} />
                      </Pressable>
                    </View>
                  ) : null}
                  {promoError ? (
                    <Text
                      testID="checkout.promo-error"
                      style={{
                        fontSize: 12,
                        fontFamily: FONTS.body,
                        color: colors.textError,
                        marginBottom: SPACING.sm,
                        marginTop: -SPACING.sm,
                      }}
                    >
                      {promoError}
                    </Text>
                  ) : null}
                  {isIndependenceCampaignApplied ? (
                    <View
                      testID="checkout.independence-campaign"
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: SPACING.sm,
                        marginBottom: SPACING.md,
                        padding: SPACING.sm,
                        borderRadius: RADIUS.md,
                        backgroundColor: `${colors.success}12`,
                      }}
                    >
                      <CheckCircle size={16} color={colors.success} strokeWidth={2} />
                      <Text
                        style={{
                          flex: 1,
                          fontSize: 14,
                          lineHeight: 19,
                          fontFamily: FONTS.body,
                          color: colors.success,
                        }}
                      >
                        {t('booking.independence_promo_applied')}
                      </Text>
                    </View>
                  ) : null}

                  <View style={{ gap: SPACING.sm, marginBottom: SPACING.md }}>
                    <RepeatPairFeeCard
                      completedPairTrips={completedPairTrips}
                      feePct={guestServiceFeePct}
                    />
                    <CoverageStayReasonCard />
                  </View>

                  {/* One line for the rental itself. `subtotalCents` IS
                  `dailyRateCents × days` (see pricing-core), so a
                  "Tarifa diaria × N" row followed by a "Subtotal" row printed
                  the same number twice. Keep the informative label, and keep
                  the authoritative value — on a recovered checkout the stored
                  subtotal wins over a re-multiplication of today's rate. */}
                  <PriceRow
                    label={
                      rentalDays > 0
                        ? `${t('booking.daily_rate')} × ${rentalDays}`
                        : t('booking.subtotal')
                    }
                    value={formatCurrency(pricing.subtotalCents)}
                    colors={colors}
                  />

                  {/* $0.00 lines read as charges on a receipt. The disclosure they
                  carried stays; the fake zero does not. */}
                  {pricing.insuranceCents > 0 ? (
                    <PriceRow
                      label={t('booking.insurance')}
                      value={formatCurrency(pricing.insuranceCents)}
                      colors={colors}
                    />
                  ) : (
                    <PriceNote
                      text={t('checkout.platform_insurance_coming_soon')}
                      colors={colors}
                    />
                  )}

                  {pricing.platformFeeCents > 0 && (
                    <PriceRow
                      label={`${t('booking.tago_fee')} (${guestServiceFeePct}%)`}
                      value={formatCurrency(pricing.platformFeeCents)}
                      colors={colors}
                      testID="checkout.platform-fee-row"
                    />
                  )}

                  {/* The processor uplift on Tago's fee is its own line, not a
                  footnote: this is the exact desglose the DB CHECK
                  (rentals_total_cents_breakdown_invariant) recomposes. */}
                  {pricing.platformFeeProcessingCents > 0 && (
                    <PriceRow
                      label={t('booking.payment_processing')}
                      value={formatCurrency(pricing.platformFeeProcessingCents)}
                      colors={colors}
                      muted
                      testID="checkout.processing-fee-row"
                    />
                  )}

                  {pricing.promoDiscountCents > 0 && (
                    <PriceRow
                      label={
                        isIndependenceCampaignApplied
                          ? t('booking.independence_discount')
                          : t('booking.discount')
                      }
                      value={`-${formatCurrency(pricing.promoDiscountCents)}`}
                      colors={colors}
                      highlight
                    />
                  )}

                  {pricing.taxCents > 0 ? (
                    <PriceRow
                      label={t('booking.iva')}
                      value={formatCurrency(pricing.taxCents)}
                      colors={colors}
                      muted
                    />
                  ) : (
                    <PriceNote
                      text={t('booking.iva_included')}
                      colors={colors}
                      testID="checkout.iva-note"
                    />
                  )}

                  {/* Refundable security deposit — a real charge Tago holds and
                  returns in full after the trip. Shown as its own line and
                  annotated so the guest understands it is not a fee. */}
                  {(pricing.depositCents ?? 0) > 0 && (
                    <>
                      <PriceRow
                        label={t('booking.security_deposit')}
                        value={formatCurrency(pricing.depositCents ?? 0)}
                        colors={colors}
                      />
                      <PriceNote
                        text={t('booking.security_deposit_note')}
                        colors={colors}
                        testID="checkout.deposit-note"
                      />
                    </>
                  )}

                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: SPACING.sm,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 17,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {t('booking.charged_today')}
                    </Text>
                    <Text
                      style={{ fontSize: 20, fontFamily: FONTS.bodyBold, color: colors.textAccent }}
                      testID="checkout.charged-today-total"
                    >
                      {formatCurrency(chargedCents)}
                    </Text>
                  </View>
                  {paymentRail === 'paypal' && chargedCents > (pricing.totalCents ?? 0) ? (
                    <Text
                      style={{
                        marginTop: SPACING.xs,
                        fontSize: 12,
                        fontFamily: FONTS.body,
                        color: colors.textMuted,
                      }}
                      testID="checkout.paypal-fee-line"
                    >
                      {t('checkout.paypal_fee_line')} ·{' '}
                      {formatCurrency(chargedCents - pricing.totalCents)}
                    </Text>
                  ) : null}

                  {/* Insurance provider attribution — required for CNR / SSF compliance */}
                  <View style={{ marginTop: SPACING.md, alignItems: 'flex-start' }}>
                    <InsurerBadge variant="inline" testID="checkout.provider-badge" />
                  </View>
                </View>
              )}

              {isEditorial ? (
                <CheckoutLegalAndAssistance
                  colors={colors}
                  horizontalMargin={checkoutHorizontalMargin}
                />
              ) : null}
            </CheckoutSummaryColumn>

            <CheckoutPaymentRail
              editorial={isEditorial}
              wide={isDesktop}
              testID="checkout.payment-rail"
            >
              {isEditorial && pricing ? (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: SPACING.md,
                    paddingBottom: SPACING.md,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.border,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 17,
                      fontFamily: FONTS.bodyBold,
                      color: colors.textPrimary,
                    }}
                  >
                    {t('booking.charged_today')}
                  </Text>
                  <Text
                    testID="checkout.rail-total"
                    style={{
                      fontSize: 22,
                      fontFamily: FONTS.bodyBold,
                      color: colors.textAccent,
                    }}
                  >
                    {formatCurrency(chargedCents)}
                  </Text>
                </View>
              ) : null}

              {/* Terms checkbox — same SPACING.md gap every other top-level block
              on this screen uses. On desktop the rail already has padding, so
              the terms sit next to the pay CTA instead of in a second card. */}
              <View
                style={{
                  marginTop: isEditorial ? 0 : SPACING.md,
                  marginHorizontal: checkoutHorizontalMargin,
                  gap: SPACING.sm,
                }}
              >
                <RentalTermsDocuments
                  terms={currentTerms ?? null}
                  loading={termsLoading}
                  onRetry={() => void refetchTerms()}
                  showPaymentDisclosure
                />
                <Checkbox
                  checked={termsAccepted}
                  disabled={!currentTerms}
                  onChange={(checked) => {
                    setTermsAccepted(checked)
                    if (checked) setTermsError(undefined)
                  }}
                  label={
                    currentTerms
                      ? t('rental_terms_flow.accept_label', { version: currentTerms.version })
                      : t('rental_terms_flow.loading_title')
                  }
                  error={termsError}
                  testID="checkout.terms-checkbox"
                />
              </View>

              {/* Payment block. The Checkbox above draws ~14pt of its own trailing
              trim (row padding + component margin), so the token here is one
              step smaller than the SPACING.md used between the other blocks:
              that lands the optical gap on the same ~20pt as the one above the
              checkbox, instead of the 40pt hole the SPACING.lg left. */}
              <View
                style={{
                  marginTop: isEditorial ? SPACING.md : SPACING.sm,
                  marginHorizontal: checkoutHorizontalMargin,
                  marginBottom: isEditorial ? 0 : SPACING.xl,
                  padding: isEditorial ? 0 : SPACING.md,
                  borderRadius: isEditorial ? 0 : RADIUS.lg,
                  borderWidth: isEditorial ? 0 : 1,
                  borderColor: colors.border,
                  backgroundColor: isEditorial ? 'transparent' : colors.surface,
                  gap: SPACING.md,
                }}
              >
                <CheckoutSecureHeader
                  title={
                    paymentRail === 'paypal'
                      ? t('checkout.paypal_secure_title')
                      : t('checkout.secure_payment_title')
                  }
                  description={
                    paymentRail === 'paypal'
                      ? t('checkout.paypal_secure_desc')
                      : t('checkout.secure_payment_desc')
                  }
                />

                {paypalOffered ? (
                  <CheckoutMethodPicker
                    method={paymentRail}
                    onChange={setPaymentRail}
                    title={t('checkout.payment_method')}
                    cardLabel={t('checkout.card')}
                    paypalLabel={t('checkout.paypal')}
                  />
                ) : null}

                {paymentRail === 'card' &&
                env.isSavedCardChargesEnabled &&
                (isLoadingPaymentMethods || savedCardsAvailableForCheckout.length > 0) ? (
                  <View style={{ gap: SPACING.sm }}>
                    <View>
                      <Text
                        style={{
                          fontSize: 15,
                          lineHeight: 20,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                      >
                        {t('checkout.saved_cards_title')}
                      </Text>
                      <Text
                        style={{
                          marginTop: 2,
                          fontSize: 12,
                          lineHeight: 17,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                        }}
                      >
                        {t('checkout.saved_cards_desc')}
                      </Text>
                    </View>

                    {isLoadingPaymentMethods ? (
                      <ActivityIndicator color={COLORS.coral.DEFAULT} />
                    ) : (
                      <View style={{ gap: SPACING.sm }}>
                        {savedCardsAvailableForCheckout.map((method) => {
                          const selected = selectedPaymentMethodId === method.id
                          return (
                            <Pressable
                              key={method.id}
                              onPress={() => {
                                setSelectedPaymentMethodId(method.id)
                                setSavedCardCvv('')
                              }}
                              accessibilityRole="radio"
                              accessibilityState={{ selected }}
                              testID={`checkout.saved-card-${method.id}`}
                              style={{
                                minHeight: 58,
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: SPACING.sm,
                                padding: SPACING.sm,
                                borderRadius: RADIUS.md,
                                borderWidth: 1,
                                borderColor: selected ? `${COLORS.coral.DEFAULT}66` : colors.border,
                                backgroundColor: selected
                                  ? `${COLORS.coral.DEFAULT}0D`
                                  : colors.surface,
                              }}
                            >
                              <CreditCard
                                size={20}
                                color={selected ? COLORS.coral.DEFAULT : colors.textSecondary}
                                strokeWidth={1.8}
                              />
                              <View style={{ flex: 1, minWidth: 0 }}>
                                <Text
                                  numberOfLines={1}
                                  style={{
                                    fontSize: 14,
                                    lineHeight: 19,
                                    fontFamily: FONTS.bodyBold,
                                    color: colors.textPrimary,
                                  }}
                                >
                                  {method.label}
                                </Text>
                                <Text
                                  numberOfLines={1}
                                  style={{
                                    marginTop: 1,
                                    fontSize: 12,
                                    lineHeight: 16,
                                    fontFamily: FONTS.body,
                                    color: colors.textSecondary,
                                  }}
                                >
                                  {paymentBrandLabel(method, t('payment_methods.card'))} ••••{' '}
                                  {method.cardLast4 ?? '----'} ·{' '}
                                  {t('payment_methods.expiry', {
                                    expiry: formatPaymentMethodExpiry(method),
                                  })}
                                </Text>
                              </View>
                              <CheckCircle
                                size={18}
                                color={selected ? COLORS.coral.DEFAULT : colors.border}
                                strokeWidth={2}
                              />
                            </Pressable>
                          )
                        })}

                        <Pressable
                          onPress={() => {
                            setSelectedPaymentMethodId(null)
                            setSavedCardCvv('')
                          }}
                          accessibilityRole="radio"
                          accessibilityState={{ selected: !usingSavedPaymentMethod }}
                          testID="checkout.use-new-card"
                          style={{
                            minHeight: 48,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: SPACING.sm,
                            padding: SPACING.sm,
                            borderRadius: RADIUS.md,
                            borderWidth: 1,
                            borderColor: !usingSavedPaymentMethod
                              ? `${COLORS.coral.DEFAULT}66`
                              : colors.border,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 14,
                              fontFamily: FONTS.bodyBold,
                              color: colors.textPrimary,
                            }}
                          >
                            {t('checkout.pay_with_new_card')}
                          </Text>
                          <CheckCircle
                            size={18}
                            color={!usingSavedPaymentMethod ? COLORS.coral.DEFAULT : colors.border}
                            strokeWidth={2}
                          />
                        </Pressable>
                      </View>
                    )}
                  </View>
                ) : null}

                {/* The gate lives at the CTA now, so say so at the CTA. The terms
                checkbox is far enough up the scroll that its inline error was
                landing off-screen. */}
                {!termsAccepted && (
                  <Text
                    testID="checkout.terms-required-hint"
                    style={{
                      fontSize: 13,
                      lineHeight: 18,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                    }}
                  >
                    {t('checkout.accept_terms_error')}
                  </Text>
                )}

                {paymentRail === 'paypal' ? (
                  <View style={{ gap: SPACING.md }} testID="checkout.paypal-panel">
                    <View
                      style={{ flexDirection: 'row', gap: SPACING.sm, alignItems: 'flex-start' }}
                    >
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: RADIUS.md,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: withAlpha(colors.info, 0.08),
                        }}
                      >
                        <Wallet size={21} color={colors.info} strokeWidth={2} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 15,
                            lineHeight: 20,
                            fontFamily: FONTS.bodyBold,
                            color: colors.textPrimary,
                          }}
                        >
                          {t('checkout.paypal_title')}
                        </Text>
                        <Text
                          style={{
                            marginTop: SPACING.xs,
                            fontSize: 13,
                            lineHeight: 18,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                          }}
                        >
                          {t('checkout.paypal_desc')}
                        </Text>
                      </View>
                    </View>

                    {hostedCheckoutUrl ? (
                      <View
                        style={{
                          gap: SPACING.sm,
                          padding: SPACING.sm,
                          borderRadius: RADIUS.md,
                          backgroundColor: `${colors.warning}12`,
                          borderWidth: 1,
                          borderColor: `${colors.warning}66`,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 13,
                            lineHeight: 18,
                            fontFamily: FONTS.body,
                            color: colors.textPrimary,
                          }}
                        >
                          {t('checkout.paypal_pending')}
                        </Text>
                        <Button
                          title={t('checkout.paypal_continue')}
                          variant="secondary"
                          size="md"
                          disabled={isPending}
                          onPress={() => void Linking.openURL(hostedCheckoutUrl)}
                          testID="checkout.paypal-continue"
                        />
                      </View>
                    ) : null}

                    <CheckoutPayFooter
                      title={t('checkout.pay_with_paypal', {
                        amount: formatCurrency(chargedCents),
                      })}
                      onPress={handlePayPalPayment}
                      loading={isProcessingPayPalPayment}
                      disabled={isPending || !hasCheckoutData || !termsAccepted}
                      testID="checkout.pay-paypal"
                      trustText={t('checkout.paypal_secure_note')}
                      hintText={t('checkout.processor_fee_hint')}
                    />
                  </View>
                ) : usingSavedPaymentMethod && selectedPaymentMethod ? (
                  <View style={{ gap: SPACING.sm }} testID="checkout.saved-card-cvv-panel">
                    <Text
                      style={{
                        fontSize: 13,
                        lineHeight: 18,
                        fontFamily: FONTS.body,
                        color: colors.textSecondary,
                      }}
                    >
                      {t('checkout.saved_card_cvv_desc')}
                    </Text>
                    <View
                      style={{
                        height: 54,
                        borderRadius: RADIUS.md,
                        borderWidth: 1,
                        borderColor: colors.border,
                        backgroundColor: colors.surfaceRaised,
                        paddingHorizontal: SPACING.md,
                        justifyContent: 'center',
                      }}
                    >
                      <RNTextInput
                        value={savedCardCvv}
                        onChangeText={(value) =>
                          setSavedCardCvv(
                            value
                              .replace(/\D/g, '')
                              .slice(0, expectedCvvLength(selectedPaymentMethod)),
                          )
                        }
                        placeholder={t('card_form.cvv')}
                        placeholderTextColor={colors.textMuted}
                        keyboardType="number-pad"
                        inputMode="numeric"
                        secureTextEntry
                        style={{
                          fontSize: 18,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                        testID="checkout.saved-card-cvv"
                        accessibilityLabel={t('checkout.saved_card_cvv_label')}
                      />
                    </View>
                    <CheckoutPayFooter
                      title={t('checkout.pay_securely', {
                        amount: formatCurrency(chargedCents),
                      })}
                      onPress={handleSavedCardPayment}
                      loading={isPaymentInFlight}
                      disabled={
                        isPending ||
                        !hasCheckoutData ||
                        !termsAccepted ||
                        savedCardCvv.length !== expectedCvvLength(selectedPaymentMethod)
                      }
                      testID="checkout.pay-saved-card"
                      trustText={t('tours.checkout.secure_note')}
                    />
                  </View>
                ) : (
                  <CardForm
                    amountCents={chargedCents}
                    defaultCardholderName={fullProfileName(profile)}
                    onSubmit={handleCardPayment}
                    loading={isPaymentInFlight}
                    disabled={isPending || !hasCheckoutData || !termsAccepted}
                    trustText={t('tours.checkout.secure_note')}
                  />
                )}
              </View>
            </CheckoutPaymentRail>
          </CheckoutEditorialRow>
          {!isEditorial ? (
            <CheckoutLegalAndAssistance
              colors={colors}
              horizontalMargin={checkoutHorizontalMargin}
            />
          ) : null}
        </KeyboardAwareView>

        <PhotoLightbox
          visible={lightbox.visible}
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={lightbox.setIndex}
          onClose={lightbox.close}
        />

        <ThreeDsModal
          visible={threeDsChallenge !== null}
          challengeUrl={threeDsChallenge?.url ?? ''}
          onComplete={handleThreeDsComplete}
          onCancel={handleThreeDsCancel}
        />

        <ConfirmDialog
          visible={isRefundDialogVisible}
          title={t('payment.refunded_title')}
          message={
            refundedPayment?.settled ? t('payment.refunded_desc') : t('payment.refund_pending_desc')
          }
          confirmLabel={t('payment.unconfirmed_action')}
          cancelLabel={t('common.close')}
          destructive={false}
          icon={<Clock size={28} color={colors.warning} strokeWidth={2} />}
          onConfirm={() => {
            setIsRefundDialogVisible(false)
            goToTripsForUnconfirmedPayment()
          }}
          onCancel={() => setIsRefundDialogVisible(false)}
          testIDPrefix="checkout.payment-refunded"
        />

        <ConfirmDialog
          visible={isUnconfirmedDialogVisible}
          title={t('payment.unconfirmed_title')}
          message={t('payment.unconfirmed_desc')}
          confirmLabel={t('payment.unconfirmed_action')}
          cancelLabel={t('common.close')}
          destructive={false}
          icon={<Clock size={28} color={colors.warning} strokeWidth={2} />}
          onConfirm={goToTripsForUnconfirmedPayment}
          onCancel={() => setIsUnconfirmedDialogVisible(false)}
          testIDPrefix="checkout.payment-unconfirmed"
        />

        <ConfirmDialog
          visible={staleHoldId !== null}
          title={t('rental_terms_flow.stale_hold_title')}
          message={t('rental_terms_flow.stale_hold_body')}
          confirmLabel={t('rental_terms_flow.restart')}
          cancelLabel={t('common.close')}
          destructive={false}
          confirmLoading={isRestartingStaleHold}
          onConfirm={() => void restartStaleHold()}
          onCancel={() => setStaleHoldId(null)}
          testIDPrefix="checkout.stale-terms"
        />

        {!isRecoveredCheckout &&
          hasVehicleHomeLocation &&
          vehicle?.homeLocation &&
          effectivePickupLocation &&
          effectiveDropoffLocation && (
            <BookingLocationPicker
              visible={locationPickerMode !== null}
              mode={locationPickerMode ?? 'pickup'}
              initialLocation={
                locationPickerMode === 'dropoff'
                  ? effectiveDropoffLocation
                  : effectivePickupLocation
              }
              hostLocation={vehicle.homeLocation}
              deliveryRadiusKm={deliveryRadiusKm}
              onClose={() => setLocationPickerMode(null)}
              onSave={handleSaveLocation}
            />
          )}
      </View>
    </ScreenContainer>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function PriceRow({
  label,
  value,
  colors,
  highlight = false,
  muted = false,
  testID,
}: {
  label: string
  value: string
  colors: ReturnType<typeof useSemanticColors>
  highlight?: boolean
  muted?: boolean
  testID?: string
}) {
  return (
    <View
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: SPACING.md,
        marginBottom: SPACING.sm,
      }}
    >
      <Text
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 14,
          lineHeight: 20,
          fontFamily: FONTS.body,
          color: muted ? colors.textMuted : colors.textSecondary,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          flexShrink: 0,
          fontSize: 14,
          lineHeight: 20,
          fontFamily: FONTS.body,
          textAlign: 'right',
          color: muted ? colors.textMuted : highlight ? colors.success : colors.textPrimary,
        }}
      >
        {value}
      </Text>
    </View>
  )
}

/**
 * A line in the breakdown that carries information but no amount — "IVA no
 * aplicado por ahora", "Cobertura próximamente". These used to be rendered as
 * PriceRows with a `$0.00` on the right, which on a price breakdown reads as a
 * charge of zero rather than as a note.
 */
function PriceNote({
  text,
  colors,
  testID,
}: {
  text: string
  colors: ReturnType<typeof useSemanticColors>
  testID?: string
}) {
  return (
    <Text
      testID={testID}
      style={{
        fontSize: 12,
        lineHeight: 17,
        fontFamily: FONTS.body,
        color: colors.textMuted,
        marginBottom: SPACING.sm,
      }}
    >
      {text}
    </Text>
  )
}

function CheckoutLocationRow({
  label,
  value,
  colors,
  onPress,
}: {
  label: string
  value: string
  colors: ReturnType<typeof useSemanticColors>
  onPress?: () => void
}) {
  const editable = Boolean(onPress)
  return (
    <Pressable
      onPress={onPress}
      disabled={!editable}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}
      style={{
        minHeight: 50,
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
      }}
    >
      <MapPin size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 12,
            lineHeight: 16,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
          numberOfLines={1}
        >
          {label}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontSize: 14,
            lineHeight: 19,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
          numberOfLines={2}
        >
          {value}
        </Text>
      </View>
      {editable ? <Pencil size={17} color={colors.textMuted} strokeWidth={2} /> : null}
    </Pressable>
  )
}

function CheckoutNotice({
  title,
  body,
  tone,
  colors,
  horizontalMargin = SPACING.md,
  actionLabel,
  onAction,
  actionTestID,
  testID,
}: {
  title: string
  body: string
  tone: 'warning' | 'error'
  colors: ReturnType<typeof useSemanticColors>
  horizontalMargin?: number
  /** Optional in-notice escape. A notice about a dead end must offer the way out. */
  actionLabel?: string
  onAction?: () => void
  actionTestID?: string
  testID?: string
}) {
  const accent = tone === 'error' ? colors.error : colors.warning
  return (
    <View
      testID={testID}
      style={{
        marginTop: SPACING.md,
        marginHorizontal: horizontalMargin,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: accent,
        backgroundColor: `${accent}12`,
        flexDirection: 'row',
        gap: SPACING.sm,
        alignItems: 'flex-start',
      }}
    >
      <AlertCircle size={19} color={accent} strokeWidth={2} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 14,
            lineHeight: 19,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {title}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontSize: 13,
            lineHeight: 18,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {body}
        </Text>
        {actionLabel && onAction ? (
          <Pressable
            onPress={onAction}
            accessibilityRole="button"
            accessibilityLabel={actionLabel}
            testID={actionTestID}
            style={{
              marginTop: SPACING.sm,
              minHeight: INTERACTION.minTouchTarget,
              justifyContent: 'center',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.bodyBold,
                color: colors.textAccent,
              }}
            >
              {actionLabel}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  )
}

function CheckoutLegalAndAssistance({
  colors,
  horizontalMargin,
}: {
  colors: ReturnType<typeof useSemanticColors>
  horizontalMargin: number
}) {
  const { t } = useTranslation()
  return (
    <>
      <View
        style={{
          marginTop: SPACING.md,
          marginHorizontal: horizontalMargin,
          padding: SPACING.md,
          backgroundColor: colors.surface,
          borderRadius: RADIUS.lg,
          borderWidth: 1,
          borderColor: colors.border,
        }}
      >
        <Text
          style={{
            fontSize: 18,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
            marginBottom: SPACING.md,
          }}
        >
          {t('checkout.cancellation_policy')}
        </Text>
        <CancellationRow
          icon={<CheckCircle size={16} color={colors.success} />}
          text={t('checkout.cancellation_full')}
          colors={colors}
        />
        <CancellationRow
          icon={<Clock size={16} color={colors.warning} />}
          text={t('checkout.cancellation_partial')}
          colors={colors}
        />
        <CancellationRow
          icon={<AlertCircle size={16} color={colors.textError} />}
          text={t('checkout.cancellation_none')}
          colors={colors}
        />
        <CancellationRow
          icon={<Tag size={16} color={colors.textSecondary} />}
          text={t('checkout.cancellation_fee_note')}
          colors={colors}
        />
      </View>
      <CoverageAssistanceCard
        compact
        testID="checkout.assistance-card"
        style={{ marginTop: SPACING.md, marginHorizontal: horizontalMargin }}
      />
    </>
  )
}

function CancellationRow({
  icon,
  text,
  colors,
}: {
  icon: React.ReactNode
  text: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        marginBottom: SPACING.sm,
      }}
    >
      {icon}
      <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary, flex: 1 }}>
        {text}
      </Text>
    </View>
  )
}
